import Sidebar from "./Sidebar";
import PagePurchaseOrders from "./PagePurchaseOrders";
import PageSourcing from "./PageSourcing";
import { useState, useEffect, useCallback, useRef } from "react";
import PageReturns from "./PageReturns";
import PageGST from "./PageGST";
import PageLogistics from "./PageLogistics";
import PageBilling from "./PageBilling";
import PageInvoicePDF from "./PageInvoicePDF";
import "./mobile-styles.css";
import WarehouseSelector from "./WarehouseSelector";
import Pagination        from "./Pagination";
import PageWMS from "./PageWMS";
import PagePIM from "./PagePIM";
import PageAccounting from "./PageAccounting";
import PageWhatsApp from "./PageWhatsApp";
import PageReconciliation from "./PageReconciliation";
import PageAutomation from "./PageAutomation";
import PageReports from "./PageReports";
import PageTeam from "./PageTeam";
import PageProfile from "./PageProfile";
import PageLabels from "./PageLabels";
import PageExpenses from "./PageExpenses";
import PageImageTools from "./PageImageTools";
import PageTasks from "./PageTasks";
import PageMyntraImages from "./PageMyntraImages";
import PageCalculator from "./PageCalculator";
import PageModules from "./PageModules";
import PageGetStarted from "./PageGetStarted";
import PageInventoryPro from "./PageInventoryPro";
import PageFinancePro from "./PageFinancePro";
import PageOrdersPro from "./PageOrdersPro";
import PageCatalogPro from "./PageCatalogPro";
import PageCustomerPro from "./PageCustomerPro";
import PageTasksPro from "./PageTasksPro";
import PageMyntraImport from "./PageMyntraImport";
import PageMyntraPnL from "./PageMyntraPnL";
import PageMarketplaceImport from "./PageMarketplaceImport";
import PageSkuMapping from "./PageSkuMapping";

const PROXY = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

// ── auth helpers ──────────────────────────────────────────────
const getToken  = () => localStorage.getItem("sellsync_token");
const setToken  = (t) => localStorage.setItem("sellsync_token", t);
const clearToken= () => localStorage.removeItem("sellsync_token");

const authFetch = async (path, opts = {}) => {
  const token = getToken();
  const res = await fetch(PROXY + path, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...opts.headers,
    },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

// ── constants ─────────────────────────────────────────────────
const PLATFORMS = [
  { id: "shopify",  name: "Shopify",  color: "#5a8a00", bg: "#f3f9ec" },
  { id: "amazon",   name: "Amazon",   color: "#c45000", bg: "#fff3e8" },
  { id: "flipkart", name: "Flipkart", color: "#1b57c0", bg: "#ebf2ff" },
  { id: "myntra",   name: "Myntra",   color: "#b5003c", bg: "#fff0f4" },
  { id: "meesho", name: "Meesho", color: "#9f2089", bg: "#fdf0fb" },
];

const NAV = [
  { id: "dashboard", label: "Dashboard"   },
  { id: "inventory", label: "Inventory"   },
  { id: "skumap",    label: "SKU Mapping" },
  { id: "orders",    label: "Orders"      },
  { id: "channels",  label: "Channels"    },
  { id: "expenses", label: "💸 Expenses" },
  { id: "imagetools", label: "🖼 Image Tools" },
  { id: "tasks", label: "✅ Tasks" },
  { id: "settings",  label: "Settings"    },
  { id: "purchases", label: "Purchase Orders" },
  { id: "returns", label: "Returns" },
  { id: "gst", label: "GST & Analytics" },
];

const PLANS = {
  free:       { label: "Free",       color: "#6b7280", daysLeft: null },
  trial:      { label: "Free Trial", color: "#6b7280", daysLeft: null },
  starter:    { label: "Starter",    color: "#2563eb" },
  growth:     { label: "Growth",     color: "#F47920" },
  enterprise: { label: "Enterprise", color: "#7c3aed" },
};

// Pages a FREE-plan user can access without paying.
// The ROAS calculator is the free hook; everything else needs a paid plan.
const FREE_PAGES = ["getstarted", "calculator", "settings", "billing", "profile", "modules"];
// Trial users get everything (so they can evaluate). Paid plans get everything.
const isLockedForPlan = (plan, page) => {
  if (plan === "free") return !FREE_PAGES.includes(page);
  return false; // trial, starter, growth, enterprise → full access
};

const INR        = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const totalStock = (inv) => Object.values(inv || {}).reduce((a, b) => a + (b || 0), 0);
const newInvId   = () => "INV-" + Date.now().toString(36).toUpperCase();
const LS_SKU     = "sellsync_sku_map";
const LS_MAN     = "sellsync_manual_inv";

const loadSkuMap    = () => { try { return JSON.parse(localStorage.getItem(LS_SKU) || "{}"); } catch { return {}; } };
const loadManualInv = () => { try { return JSON.parse(localStorage.getItem(LS_MAN) || "{}"); } catch { return {}; } };
const saveSkuMap    = (m) => localStorage.setItem(LS_SKU, JSON.stringify(m));
const saveManualInv = (m) => localStorage.setItem(LS_MAN, JSON.stringify(m));

function stockStatus(inv, low = 10) {
  const t = totalStock(inv);
  if (t === 0) return { label: "Out of Stock", color: "#b91c1c", bg: "#fef2f2" };
  if (PLATFORMS.some((p) => (inv[p.id] || 0) > 0 && (inv[p.id] || 0) <= low))
    return { label: "Low Stock", color: "#b45309", bg: "#fffbeb" };
  return { label: "In Stock", color: "#15803d", bg: "#f0fdf4" };
}

function mapShopifyProduct(p, invMap, manualInv, skuMap) {
  const shopifySku = p.variants?.[0]?.sku || String(p.id);
  const mapping    = skuMap[shopifySku]   || {};
  const manual     = manualInv[shopifySku]|| {};
  return {
    id: shopifySku, shopifyId: p.id, name: p.title, shopifySku,
    amazonSku:   mapping.amazonSku   || "",
    flipkartSku: mapping.flipkartSku || "",
    myntraSku:   mapping.myntraSku   || "",
    price:       parseFloat(p.variants?.[0]?.price || 0),
    category:    p.product_type || "General",
    imageUrl:    p.image?.src || null,
    inventoryItemId: p.variants?.[0]?.inventory_item_id,
    inventory: {
      shopify:  invMap[p.variants?.[0]?.inventory_item_id] ?? (p.variants?.[0]?.inventory_quantity || 0),
      amazon:   manual.amazon   ?? 0,
      flipkart: manual.flipkart ?? 0,
      myntra:   manual.myntra   ?? 0,
    },
    lowAlert: 10,
  };
}

// ── shared styles ─────────────────────────────────────────────
const S = {
  wrap:    { display:"flex", flexDirection:"column", height:"100vh", fontFamily:"'Segoe UI',sans-serif", fontSize:13, color:"#111", background:"#f5f6fa" },
  sidebar: { width:200, minWidth:200, background:"#fff", borderRight:"1px solid #e5e7eb", display:"flex", flexDirection:"column" },
  logo:    { padding:"14px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", gap:8 },
  logoBox: { width:30, height:30, borderRadius:6, background:"#F47920", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:15 },
  navSec:  { fontSize:10, color:"#9ca3af", padding:"12px 16px 4px", letterSpacing:"0.6px", textTransform:"uppercase" },
  navItem: (a) => ({ display:"flex", alignItems:"center", padding:"9px 16px", cursor:"pointer", color:a?"#F47920":"#6b7280", background:a?"#fff8f2":"transparent", borderLeft:a?"3px solid #F47920":"3px solid transparent", fontWeight:a?600:400, transition:"all .15s" }),
  topbar:  { height:50, background:"#fff", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", padding:"0 20px", gap:10 },
  content: { flex:1, overflowY:"auto", padding:20 },
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  kpiGrid: { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 },
  kpi:     { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px" },
  btn:     (c="#F47920") => ({ padding:"6px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"6px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:   { padding:"6px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none" },
  qtyInp:  (v,low) => ({ width:64, textAlign:"center", padding:"4px 6px", borderRadius:6, border:`1px solid ${v===0?"#fca5a5":v<=low?"#fcd34d":"#d1d5db"}`, fontSize:13, fontFamily:"inherit", outline:"none" }),
  th:      { padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb", whiteSpace:"nowrap" },
  td:      { padding:"9px 12px", borderBottom:"1px solid #f3f4f6", verticalAlign:"middle" },
  fLabel:  { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:4 },
  fInput:  { width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", marginBottom:12 },
  overlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" },
  modal:   { background:"#fff", borderRadius:12, padding:24, width:480, maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" },
  authWrap:{ minHeight:"100vh", background:"#f5f6fa", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Segoe UI',sans-serif" },
  authCard:{ background:"#fff", borderRadius:14, padding:"36px 40px", width:420, maxWidth:"95vw", boxShadow:"0 4px 24px rgba(0,0,0,0.08)" },
};

const Badge = ({ label, color, bg }) => (
  <span style={{ background:bg, color, padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:600, whiteSpace:"nowrap" }}>{label}</span>
);

// ══════════════════════════════════════════════════════════════
// AUTH PAGES
// ══════════════════════════════════════════════════════════════

function AuthPage({ onLogin }) {
  const [mode,    setMode]    = useState("login"); // login | register | forgot
  const [form,    setForm]    = useState({ storeName:"", email:"", password:"" });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");

  // handle Google redirect
  useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const token  = params.get("token");
  if (token) {
    setToken(token);
    window.history.replaceState({}, "", "/");
    authFetch("/auth/me")
      .then((data) => onLogin(data.tenant))
      .catch(() => onLogin(null));
    return;
  }
  if (params.get("verified")) setSuccess("Email verified! Please log in.");
  if (params.get("error"))    setError("Google login failed. Please try again.");
}, [onLogin]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    setLoading(true);
    try {
      if (mode === "login") {
        const data = await authFetch("/auth/login", { method:"POST", body: JSON.stringify({ email:form.email, password:form.password }) });
        setToken(data.token);
        onLogin(data.tenant);
      } else if (mode === "register") {
        const data = await authFetch("/auth/register", { method:"POST", body: JSON.stringify(form) });
        setToken(data.token);
        onLogin(data.tenant);
      } else {
        await authFetch("/auth/forgot-password", { method:"POST", body: JSON.stringify({ email:form.email }) });
        setSuccess("Reset link sent! Check your email.");
      }
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  const titles = { login:"Welcome back", register:"Start your free trial", forgot:"Reset password" };

  return (
    <div style={S.authWrap}>
      <div className="auth-card" style={S.authCard}>
        {/* logo */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:28 }}>
          <div style={{ ...S.logoBox, width:36, height:36, fontSize:18 }}>3</div>
          <div>
            <div style={{ fontWeight:700, fontSize:18 }}>3June</div>
            <div style={{ fontSize:11, color:"#9ca3af" }}>Profit on every channel</div>
          </div>
        </div>

        <div style={{ fontWeight:700, fontSize:20, marginBottom:6 }}>{titles[mode]}</div>
        {mode === "register" && <div style={{ fontSize:12, color:"#6b7280", marginBottom:20 }}>14-day free trial · No credit card required</div>}
        {mode !== "register" && <div style={{ marginBottom:20 }}/>}

        {error   && <div style={{ background:"#fef2f2", color:"#b91c1c", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:14, border:"1px solid #fecaca" }}>{error}</div>}
        {success && <div style={{ background:"#f0fdf4", color:"#15803d", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:14, border:"1px solid #bbf7d0" }}>{success}</div>}

        <form onSubmit={submit}>
          {mode === "register" && (
            <>
              <label style={S.fLabel}>Store / Brand Name</label>
              <input style={S.fInput} placeholder="e.g. Leather Patta" value={form.storeName}
                onChange={(e) => setForm({ ...form, storeName:e.target.value })} required/>
            </>
          )}
          <label style={S.fLabel}>Email</label>
          <input style={S.fInput} type="email" placeholder="you@yourstore.com" value={form.email}
            onChange={(e) => setForm({ ...form, email:e.target.value })} required/>
          {mode !== "forgot" && (
            <>
              <label style={S.fLabel}>Password</label>
              <input style={S.fInput} type="password" placeholder={mode==="register"?"Min 8 characters":"••••••••"} value={form.password}
                onChange={(e) => setForm({ ...form, password:e.target.value })} required minLength={8}/>
            </>
          )}
          <button type="submit" style={{ ...S.btn(), width:"100%", padding:"11px", fontSize:14, marginBottom:14, opacity:loading?0.7:1 }} disabled={loading}>
            {loading ? "Please wait…" : mode==="login" ? "Log in" : mode==="register" ? "Start free trial" : "Send reset link"}
          </button>
        </form>

        {/* Google login */}
        {mode !== "forgot" && (
          <>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
              <div style={{ flex:1, height:1, background:"#e5e7eb" }}/>
              <span style={{ fontSize:11, color:"#9ca3af" }}>or</span>
              <div style={{ flex:1, height:1, background:"#e5e7eb" }}/>
            </div>
            <a href={`${process.env.REACT_APP_API_URL?.replace("/api","") || "http://localhost:4000"}/api/auth/google`}style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, width:"100%", padding:"10px", borderRadius:8, border:"1px solid #d1d5db", background:"#fff", cursor:"pointer", fontSize:13, fontWeight:500, color:"#374151", textDecoration:"none", marginBottom:16 }}>
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Continue with Google
            </a>
          </>
        )}

        {/* mode switcher */}
        <div style={{ fontSize:12, color:"#6b7280", textAlign:"center" }}>
          {mode === "login" && <>Don't have an account? <span style={{ color:"#F47920", cursor:"pointer", fontWeight:600 }} onClick={() => { setMode("register"); setError(""); }}>Sign up free</span></>}
          {mode === "login" && <> · <span style={{ color:"#F47920", cursor:"pointer", fontWeight:600 }} onClick={() => { setMode("forgot"); setError(""); }}>Forgot password?</span></>}
          {mode === "register" && <>Already have an account? <span style={{ color:"#F47920", cursor:"pointer", fontWeight:600 }} onClick={() => { setMode("login"); setError(""); }}>Log in</span></>}
          {mode === "forgot" && <><span style={{ color:"#F47920", cursor:"pointer", fontWeight:600 }} onClick={() => { setMode("login"); setError(""); }}>Back to login</span></>}
        </div>
      </div>

      

    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SETTINGS PAGE
// ══════════════════════════════════════════════════════════════
function PageSettings({ tenant, onLogout, toast }) {
  const [platForm, setPlatForm] = useState({
    platform:"shopify", store:"", token:"",
    clientId:"", clientSecret:"", refreshToken:"",
    sellerId:"", marketplaceId:"A21TJRUUN4KGV",
    apiKey:"", secret:"",
  });
  const [saving,  setSaving]  = useState(false);
  const [profile, setProfile] = useState({ storeName: tenant?.storeName||"", phone:"" });
  const plan      = PLANS[tenant?.plan] || PLANS.trial;
  const trialDays = tenant?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(tenant.trialEndsAt) - Date.now()) / 86400000))
    : 0;

  const connectPlatform = async () => {
    setSaving(true);
    try {
      let creds = {};
      if (platForm.platform === "shopify") {
        if (!platForm.store || !platForm.token) { toast("Enter store URL and token", "error"); setSaving(false); return; }
        creds = { store: platForm.store, token: platForm.token };
      } else if (platForm.platform === "amazon") {
        if (!platForm.clientId || !platForm.refreshToken || !platForm.sellerId) { toast("Fill all Amazon fields", "error"); setSaving(false); return; }
        creds = { clientId: platForm.clientId, clientSecret: platForm.clientSecret, refreshToken: platForm.refreshToken, sellerId: platForm.sellerId, marketplaceId: platForm.marketplaceId };
      } else if (platForm.platform === "flipkart") {
        if (!platForm.apiKey || !platForm.secret) { toast("Enter API Key and Secret", "error"); setSaving(false); return; }
        creds = { apiKey: platForm.apiKey, secret: platForm.secret };
      } else if (platForm.platform === "myntra") {
        if (!platForm.clientId || !platForm.secret) { toast("Enter Client ID and Secret", "error"); setSaving(false); return; }
        creds = { clientId: platForm.clientId, secret: platForm.secret };
      }
      await authFetch("/auth/platforms", {
        method: "PUT",
        body: JSON.stringify({ platform: platForm.platform, credentials: creds }),
      });
      toast(platForm.platform + " connected ✓", "success");
      setPlatForm((f) => ({ ...f, token:"", secret:"", clientSecret:"", refreshToken:"" }));
    } catch(err) { toast(err.message, "error"); }
    finally { setSaving(false); }
  };

  if (!tenant) return <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>Loading settings…</div>;

  return (
    <div>
      {/* plan banner */}
      <div style={{ ...S.card, padding:20, marginBottom:14, borderLeft:`4px solid ${plan.color}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:16 }}>{tenant?.storeName}</div>
            <div style={{ fontSize:12, color:"#6b7280", marginTop:2 }}>{tenant?.email}</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <Badge label={plan.label} color={plan.color} bg={plan.color+"18"}/>
            {tenant?.plan === "trial" && (
              <div style={{ fontSize:11, color:"#d97706", marginTop:4 }}>⏳ {trialDays} days left in trial</div>
            )}
          </div>
        </div>
        {tenant?.plan === "trial" && (
          <button style={{ ...S.btn(), marginTop:14 }} onClick={() => toast("Go to Billing & Plans to upgrade!", "info")}>
            ⚡ Upgrade Plan
          </button>
        )}
      </div>

      {/* platform connection status */}
      <div style={S.card}>
        <div style={S.cardHdr}>Platform Connection Status</div>
        <div style={{ padding:16, display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10 }}>
          {PLATFORMS.map((pl) => {
            const connected = tenant?.platforms?.[pl.id]?.connected;
            return (
              <div key={pl.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", borderRadius:8, border:`1px solid ${connected?"#bbf7d0":"#e5e7eb"}`, background:connected?"#f0fdf4":"#f9fafb" }}>
                <span style={{ fontWeight:600, color:pl.color }}>{pl.name}</span>
                <span style={{ fontSize:12, fontWeight:600, color:connected?"#15803d":"#9ca3af" }}>
                  {connected ? "✓ Connected" : "Not connected"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* connect platforms */}
      <div style={S.card}>
        <div style={S.cardHdr}>Connect / Update Platform</div>
        <div style={{ padding:16 }}>
          <label style={S.fLabel}>Select Platform</label>
          <select style={S.fInput} value={platForm.platform} onChange={(e) => setPlatForm({ ...platForm, platform:e.target.value, store:"", token:"", clientId:"", clientSecret:"", refreshToken:"", sellerId:"", apiKey:"", secret:"" })}>
            {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.name} {tenant?.platforms?.[p.id]?.connected ? "✓" : ""}</option>)}
            <option value="meesho">Meesho {tenant?.platforms?.meesho?.connected ? "✓" : ""}</option>
          </select>

          {/* SHOPIFY */}
          {platForm.platform === "shopify" && (
            <div style={{ background:"#f3f9ec", borderRadius:8, padding:14, marginBottom:12 }}>
              <div style={{ fontSize:12, color:"#5a8a00", fontWeight:600, marginBottom:10 }}>🏪 Shopify Credentials</div>
              <label style={S.fLabel}>Store URL</label>
              <input style={S.fInput} placeholder="your-store.myshopify.com" value={platForm.store} onChange={(e) => setPlatForm({ ...platForm, store:e.target.value })}/>
              <label style={S.fLabel}>Admin API Access Token</label>
              <input style={S.fInput} type="password" placeholder="shpat_xxxxxxxxxx" value={platForm.token} onChange={(e) => setPlatForm({ ...platForm, token:e.target.value })}/>
              <div style={{ fontSize:11, color:"#6b7280" }}>
                Find at: <strong>Shopify Admin → Settings → Apps → Develop apps → your app → API credentials</strong>
              </div>
            </div>
          )}

          {/* AMAZON */}
          {platForm.platform === "amazon" && (
            <div style={{ background:"#fff3e8", borderRadius:8, padding:14, marginBottom:12 }}>
              <div style={{ fontSize:12, color:"#c45000", fontWeight:600, marginBottom:10 }}>📦 Amazon SP-API Credentials</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div>
                  <label style={S.fLabel}>Client ID</label>
                  <input style={S.fInput} placeholder="amzn1.application-oa2-client.xxx" value={platForm.clientId} onChange={(e) => setPlatForm({ ...platForm, clientId:e.target.value })}/>
                </div>
                <div>
                  <label style={S.fLabel}>Client Secret</label>
                  <input style={S.fInput} type="password" placeholder="amzn1.oa2-cs.v1.xxx" value={platForm.clientSecret} onChange={(e) => setPlatForm({ ...platForm, clientSecret:e.target.value })}/>
                </div>
                <div>
                  <label style={S.fLabel}>Refresh Token</label>
                  <input style={S.fInput} type="password" placeholder="Atzr|xxxxxxxxxx" value={platForm.refreshToken} onChange={(e) => setPlatForm({ ...platForm, refreshToken:e.target.value })}/>
                </div>
                <div>
                  <label style={S.fLabel}>Seller ID (Merchant Token)</label>
                  <input style={S.fInput} placeholder="A1XXXXXXXXXX" value={platForm.sellerId} onChange={(e) => setPlatForm({ ...platForm, sellerId:e.target.value })}/>
                </div>
                <div>
                  <label style={S.fLabel}>Marketplace ID</label>
                  <input style={S.fInput} placeholder="A21TJRUUN4KGV (India)" value={platForm.marketplaceId} onChange={(e) => setPlatForm({ ...platForm, marketplaceId:e.target.value })}/>
                </div>
              </div>
              <div style={{ fontSize:11, color:"#6b7280", marginTop:8 }}>
                Find at: <strong>sellercentral.amazon.in → Apps & Services → Develop Apps</strong>
              </div>
            </div>
          )}

          {/* FLIPKART */}
          {platForm.platform === "flipkart" && (
            <div style={{ background:"#ebf2ff", borderRadius:8, padding:14, marginBottom:12 }}>
              <div style={{ fontSize:12, color:"#1b57c0", fontWeight:600, marginBottom:10 }}>🛒 Flipkart Seller API Credentials</div>
              <label style={S.fLabel}>API Key</label>
              <input style={S.fInput} placeholder="Your Flipkart API Key" value={platForm.apiKey} onChange={(e) => setPlatForm({ ...platForm, apiKey:e.target.value })}/>
              <label style={S.fLabel}>API Secret</label>
              <input style={S.fInput} type="password" placeholder="Your Flipkart Secret" value={platForm.secret} onChange={(e) => setPlatForm({ ...platForm, secret:e.target.value })}/>
              <div style={{ fontSize:11, color:"#6b7280" }}>
                Find at: <strong>seller.flipkart.com → Settings → API Access</strong>
              </div>
            </div>
          )}

          {/* MYNTRA */}
          {platForm.platform === "myntra" && (
            <div style={{ background:"#fff0f4", borderRadius:8, padding:14, marginBottom:12 }}>
              <div style={{ fontSize:12, color:"#b5003c", fontWeight:600, marginBottom:10 }}>👗 Myntra Partner API Credentials</div>
              <label style={S.fLabel}>Client ID</label>
              <input style={S.fInput} placeholder="Your Myntra Client ID" value={platForm.clientId} onChange={(e) => setPlatForm({ ...platForm, clientId:e.target.value })}/>
              <label style={S.fLabel}>Client Secret</label>
              <input style={S.fInput} type="password" placeholder="Your Myntra Secret" value={platForm.secret} onChange={(e) => setPlatForm({ ...platForm, secret:e.target.value })}/>
              <div style={{ fontSize:11, color:"#6b7280" }}>
                Find at: <strong>partnerportal.myntra.com → Settings → API Access</strong>
              </div>
            </div>
          )}

          {platForm.platform === "meesho" && (
            <div style={{ background:"#fff0f4", borderRadius:8, padding:14, marginBottom:12 }}>
              <div style={{ fontSize:12, color:"#b5003c", fontWeight:600, marginBottom:10 }}>
                🛍️ Meesho Supplier API Credentials
              </div>
             <label style={S.fLabel}>Client ID</label>
              <input style={S.fInput} placeholder="Your Meesho Client ID"
                value={platForm.clientId}
                onChange={(e) => setPlatForm({ ...platForm, clientId:e.target.value })}/>
              <label style={S.fLabel}>Secret Key</label>
              <input style={S.fInput} type="password" placeholder="Your Meesho Secret Key"
               value={platForm.secret}
                onChange={(e) => setPlatForm({ ...platForm, secret:e.target.value })}/>
             <label style={S.fLabel}>Supplier ID</label>
             <input style={S.fInput} placeholder="Your Meesho Supplier ID"
               value={platForm.sellerId}
               onChange={(e) => setPlatForm({ ...platForm, sellerId:e.target.value })}/>
              <div style={{ fontSize:11, color:"#6b7280", marginBottom:10 }}>
               To get credentials: Email <strong>meesholink-integration@meesho.com</strong>
                with subject "Request for API Credentials" and your Supplier ID.
               Find at: <strong>supplier.meesho.com → Settings → API Access</strong>
             </div>
            </div>
          )}

          <button style={{ ...S.btn(), padding:"10px 20px", fontSize:14 }} onClick={connectPlatform} disabled={saving}>
            {saving ? "Connecting…" : `Connect ${PLATFORMS.find((p)=>p.id===platForm.platform)?.name || platForm.platform}`}
          </button>
        </div>
      </div>

      <button style={{ ...S.btnOut, color:"#dc2626", borderColor:"#fecaca" }} onClick={onLogout}>Log out</button>

      
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// RESET PASSWORD PAGE  (shown when user clicks email link)
// ══════════════════════════════════════════════════════════════

function ResetPasswordPage({ onLogin }) {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [done,      setDone]      = useState(false);
  const [showPw,    setShowPw]    = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8)  return setError("Password must be at least 8 characters.");
    if (password !== confirm)  return setError("Passwords do not match.");
    setLoading(true);
    try {
      const data = await authFetch("/auth/reset-password", {
        method: "POST",
        body:   JSON.stringify({ token, password }),
      });
      setToken(data.token);
      setDone(true);
      setTimeout(() => { onLogin(data.tenant); }, 1500);
    } catch(err) {
      setError(err.message || "Link is invalid or has expired. Please request a new one.");
    } finally { setLoading(false); }
  };

  return (
    <div style={S.authWrap}>
      <div className="auth-card" style={S.authCard}>
        {/* Logo */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:28 }}>
          <div style={{ ...S.logoBox, width:36, height:36, fontSize:18 }}>3</div>
          <div>
            <div style={{ fontWeight:700, fontSize:18 }}>3June</div>
            <div style={{ fontSize:11, color:"#9ca3af" }}>Profit on every channel</div>
          </div>
        </div>

        {done ? (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
            <div style={{ fontWeight:700, fontSize:18, marginBottom:6 }}>Password updated!</div>
            <div style={{ fontSize:13, color:"#9ca3af" }}>Logging you in…</div>
          </div>
        ) : (
          <>
            <div style={{ fontWeight:700, fontSize:20, marginBottom:6 }}>Set new password</div>
            <div style={{ fontSize:12, color:"#9ca3af", marginBottom:20 }}>Choose a strong password for your account.</div>

            {!token && (
              <div style={{ background:"#fef2f2", color:"#b91c1c", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:14, border:"1px solid #fecaca" }}>
                ❌ No reset token found. Please use the link from your email.
              </div>
            )}
            {error && (
              <div style={{ background:"#fef2f2", color:"#b91c1c", padding:"10px 14px", borderRadius:8, fontSize:13, marginBottom:14, border:"1px solid #fecaca" }}>
                {error}
              </div>
            )}

            <form onSubmit={submit}>
              <label style={S.fLabel}>New password</label>
              <div style={{ position:"relative", marginBottom:14 }}>
                <input
                  type={showPw ? "text" : "password"}
                  style={{ ...S.fInput, paddingRight:40 }}
                  placeholder="Minimum 8 characters"
                  value={password}
                  onChange={e=>setPassword(e.target.value)}
                  required/>
                <span
                  onClick={()=>setShowPw(v=>!v)}
                  style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", cursor:"pointer", fontSize:16, color:"#9ca3af" }}>
                  {showPw ? "🙈" : "👁"}
                </span>
              </div>

              <label style={S.fLabel}>Confirm new password</label>
              <input
                type={showPw ? "text" : "password"}
                style={{ ...S.fInput, marginBottom:20 }}
                placeholder="Repeat password"
                value={confirm}
                onChange={e=>setConfirm(e.target.value)}
                required/>

              {/* Password strength indicator */}
              {password.length > 0 && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ display:"flex", gap:4, marginBottom:4 }}>
                    {[1,2,3,4].map(i => (
                      <div key={i} style={{
                        flex:1, height:3, borderRadius:2,
                        background: password.length >= i*3 && (i<3||/[A-Z]/.test(password)||/[0-9]/.test(password))
                          ? i===4?"#15803d":i===3?"#22c55e":i===2?"#d97706":"#ef4444"
                          : "#e5e7eb"
                      }}/>
                    ))}
                  </div>
                  <div style={{ fontSize:11, color:"#9ca3af" }}>
                    {password.length < 6 ? "Too short" : password.length < 10 ? "Weak — add numbers/capitals" : /[A-Z]/.test(password)&&/[0-9]/.test(password) ? "Strong ✓" : "Good — add capitals or numbers"}
                  </div>
                </div>
              )}

              <button
                type="submit"
                style={{ ...S.btn(), width:"100%", padding:"12px", fontSize:15, opacity: loading ? 0.7 : 1 }}
                disabled={loading || !token}>
                {loading ? "Updating…" : "Set new password"}
              </button>
            </form>

            <div style={{ textAlign:"center", marginTop:16, fontSize:13, color:"#9ca3af" }}>
              Remembered it?{" "}
              <a href="/" style={{ color:"#F47920", textDecoration:"none", fontWeight:600 }}>Back to login</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════════════════════
// ── Platform Picker — seller chooses which marketplaces they sell on ──
function PlatformPicker({ onConfirm, brandName }) {
  const [sel, setSel] = useState([]);
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  // Depth status — honest about which platforms are fully built
  const DEPTH = { myntra: "full", shopify: "full", amazon: "beta", flipkart: "beta", meesho: "beta" };
  return (
    <div style={{ minHeight: "100vh", background: "#FDF8F2", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 620, width: "100%", background: "#fff", borderRadius: 18, border: "1px solid #e8ddd0", padding: "40px 44px", boxShadow: "0 20px 60px rgba(15,26,15,0.1)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <div style={{ width: 42, height: 42, background: "#F47920", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 22, color: "#fff", fontFamily: "Georgia,serif" }}>3</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#0F1A0F" }}>Welcome{brandName ? ", " + brandName : ""}!</div>
        </div>
        <div style={{ fontSize: 15, color: "#6b5d4d", lineHeight: 1.6, marginBottom: 28 }}>
          Where do you sell? Pick your platforms — 3June will tailor everything (dashboard, P&amp;L, reports) to just those. You can change this anytime in Settings.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 28 }}>
          {PLATFORMS.map((p) => {
            const on = sel.includes(p.id);
            const depth = DEPTH[p.id] || "beta";
            return (
              <button key={p.id} onClick={() => toggle(p.id)} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                padding: "16px 18px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit",
                border: "2px solid " + (on ? p.color : "#e8ddd0"),
                background: on ? p.bg : "#fff", textAlign: "left", transition: "all 0.15s",
              }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: on ? p.color : "#3d3020" }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                    {depth === "full" ? "✓ Full profit analytics" : "Order sync · deep P&L coming"}
                  </div>
                </div>
                <div style={{ width: 24, height: 24, borderRadius: "50%", border: "2px solid " + (on ? p.color : "#d1c7b8"), background: on ? p.color : "#fff", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                  {on ? "✓" : ""}
                </div>
              </button>
            );
          })}
        </div>

        <button onClick={() => sel.length > 0 && onConfirm(sel)} disabled={sel.length === 0} style={{
          width: "100%", padding: "14px", borderRadius: 10, border: "none", fontFamily: "inherit",
          fontSize: 15, fontWeight: 700, cursor: sel.length > 0 ? "pointer" : "not-allowed",
          background: sel.length > 0 ? "#F47920" : "#e8ddd0", color: "#fff",
          boxShadow: sel.length > 0 ? "0 4px 14px rgba(244,121,32,0.3)" : "none",
        }}>
          {sel.length === 0 ? "Pick at least one platform" : `Continue with ${sel.length} platform${sel.length > 1 ? "s" : ""} →`}
        </button>
        <div style={{ fontSize: 11, color: "#9ca3af", textAlign: "center", marginTop: 14 }}>
          Myntra has the deepest analytics today. Other platforms get order sync now, with full P&amp;L rolling out — driven by what sellers ask for.
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tenant,    setTenant]    = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [page,      setPage]      = useState(() => localStorage.getItem("ss_page") || "dashboard");
  const [activeModule, setActiveModule] = useState(() => localStorage.getItem("ss_module") || "dashboard");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("ss_viewmode") || "starter");
  // ── Opted platforms — the master filter. Seller picks which platforms they sell on. ──
  const [optedPlatforms, setOptedPlatforms] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem("3june_opted_platforms") || "null"); return Array.isArray(v) ? v : null; }
    catch { return null; }
  });
  const saveOpted = (arr) => {
    setOptedPlatforms(arr);
    localStorage.setItem("3june_opted_platforms", JSON.stringify(arr));
  };
  // Has the seller chosen yet? (null = not chosen, [] = chose none)
  const hasOpted = Array.isArray(optedPlatforms) && optedPlatforms.length > 0;
  // Helper: is a platform active for this seller?
  const isOpted = (pid) => !hasOpted || optedPlatforms.includes(pid);
  // The platforms this seller actually uses — drives the whole UI
  const myPlatforms = hasOpted ? PLATFORMS.filter((p) => optedPlatforms.includes(p.id)) : PLATFORMS;
  const setAppMode = (m) => { setViewMode(m); localStorage.setItem("ss_viewmode", m); };
  // Which modules show in Starter mode — the core daily-use set
  const STARTER_MODULES = ["dashboard", "orders", "inventory", "finance", "settings"];
  const [products,  setProducts]  = useState([]);
  const [orders,    setOrders]    = useState([]);
  const [invoices,  setInvoices]  = useState([]);
  const [invoiceOrder,  setInvoiceOrder]  = useState(null);
  const [urgentTasks,   setUrgentTasks]   = useState([]);
  const [showUrgent,    setShowUrgent]    = useState(true);
  const [urgentMinimized, setUrgentMinimized] = useState(false); // order to pre-fill invoice
  const [locationId,setLocationId]= useState(null);
  const [loading,   setLoading]   = useState(false);
  const [search,    setSearch]    = useState("");
  const [syncing,   setSyncing]   = useState(false);
  const [syncStatus,setSyncStatus]= useState({ shopify:"idle", amazon:"idle", flipkart:"idle" });
  const [toasts,    setToasts]    = useState([]);
  const [skuMap,    setSkuMap]    = useState(loadSkuMap);
  const [skuEdits,  setSkuEdits]  = useState({});
  const [invModal,  setInvModal]  = useState(false);
  const [invProd,   setInvProd]   = useState(null);
  const [invPlat,   setInvPlat]   = useState("shopify");
  const [invQty,    setInvQty]    = useState(1);
  const [invCust,   setInvCust]   = useState("");
  const [invAddr,   setInvAddr]   = useState("");
  const [locations,     setLocations]     = useState([]);
  const [currentPage,   setCurrentPage]   = useState(1);
  const [pageSize,      setPageSize]      = useState(50);
  const [totalProducts, setTotalProducts] = useState(0);

  const toast = useCallback((msg, type = "info") => {
    const id = Date.now();
    setToasts((p) => [{ id, msg, type }, ...p.slice(0, 3)]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 4500);
  }, []);

  // ── check existing session on load ──────────────────────────
  // Save current page and module to localStorage on change
  useEffect(() => {
    localStorage.setItem("ss_page", page);
    // Map page to module
    const moduleMap = {
      dashboard:"dashboard", calculator:"dashboard", getstarted:"dashboard",
      // Orders
      orders:"orders", invoicepdf:"orders", orderspro:"orders",
      // Inventory
      inventory:"inventory", wms:"inventory", inventorypro:"inventory",
      // Procurement
      purchases:"procurement", returns:"procurement", sourcing:"procurement",
      // Catalog
      pim:"catalog", skumap:"catalog", myntraImages:"catalog", catalogpro:"catalog",
      // Channels
      channels:"channels", logistics:"channels", myntraimport:"channels", myntrapnl:"channels", marketplaceimport:"channels", skumapping:"channels",
      // Finance
      gst:"finance", expenses:"finance", accounting:"finance", reconciliation:"finance", reports:"finance", financepro:"finance",
      // Settings
      whatsapp:"settings", billing:"settings", settings:"settings", team:"settings", profile:"settings", modules:"settings",
      // Operations
      automation:"operations", tasks:"operations", labels:"operations", taskspro:"operations",
      // Customers
      customerpro:"procurement",
    };
    const mod = moduleMap[page] || "dashboard";
    setActiveModule(mod);
    localStorage.setItem("ss_module", mod);
  }, [page]);

  useEffect(() => {
    const token = getToken();
    if (!token) { setAuthReady(true); return; }
    authFetch("/auth/me")
      .then((data) => { setTenant(data.tenant); setAuthReady(true); })
      .catch(() => { clearToken(); setAuthReady(true); });
  }, []);

  const handleLogin = useCallback((t) => {
  if (t) {
    setTenant(t);
    setAuthReady(true);
    // First-time users → send to Get Started instead of empty dashboard
    if (!localStorage.getItem("ss_onboarded")) {
      setActiveModule("dashboard");
      setPage("getstarted");
    }
  } else {
    const token = getToken();
    if (token) {
      authFetch("/auth/me")
        .then((data) => { setTenant(data.tenant); setAuthReady(true); })
        .catch(() => { clearToken(); setAuthReady(true); });
    } else {
      setAuthReady(true);
    }
  }
}, []);

  useEffect(() => {
    if (page && page !== "getstarted") {
      localStorage.setItem("ss_onboarded", "1");
    }
  }, [page]);

  // Trial & free users are locked to Starter mode (Advanced is a paid feature)
  const lockedToStarter = tenant?.plan === "trial" || tenant?.plan === "free";
  const effectiveMode = lockedToStarter ? "starter" : viewMode;

  useEffect(() => {
    if (lockedToStarter && viewMode !== "starter") setViewMode("starter");
  }, [lockedToStarter, viewMode]);

  // If in Starter mode and the active module is hidden, bounce to Dashboard
  useEffect(() => {
    if (effectiveMode === "starter" && !STARTER_MODULES.includes(activeModule)) {
      setActiveModule("dashboard");
      setPage("dashboard");
    }
  }, [effectiveMode, activeModule]);

  const handleLogout = () => {
    localStorage.removeItem("ss_page"); clearToken(); setTenant(null); setProducts([]); setOrders([]); };

const loadAll = useCallback(async () => {
  if (!tenant) return;

  // ── Demo account: load mock data from server, skip real platform sync ──
  if (tenant.isDemo) {
    try {
      const pd = await authFetch("/products");
      setProducts((pd.products||[]).map(p=>({
        id:p.id, name:p.title, shopifyId:p.id,
        shopifySku: p.variants?.[0]?.sku||"",
        price: parseFloat(p.variants?.[0]?.price||0),
        imageUrl: p.image?.src||"",
        inventory:{ shopify:p.variants?.[0]?.inventory_quantity||0, amazon:Math.floor(Math.random()*30)+5, flipkart:Math.floor(Math.random()*20)+2, myntra:0, meesho:0 },
        lowAlert:5, category:"Formal Belts",
      })));
      const od = await authFetch("/orders?limit=50");
      setOrders(od.orders||[]);
      setSyncStatus({ shopify:"ok", amazon:"ok", flipkart:"ok", myntra:"idle", meesho:"idle" });
    } catch(e) { console.error("Demo data error:", e.message); }
    return;
  }

  // Rate limit: skip platform API calls if synced within last 5 minutes
  const _now = Date.now();
  const _last = window.__sellsyncLastSync || 0;
  const skipPlatformSync = (_now - _last) < 300000; // 5 min cooldown
  if (!skipPlatformSync) window.__sellsyncLastSync = _now;
  const skuMapLocal = loadSkuMap();
  const manualInv   = loadManualInv();
  try {
    setLoading(true);
    setSyncStatus((s) => ({ ...s, shopify:"syncing" }));

    const locData = await authFetch("/locations");
    const locs    = locData.locations || [];
    const savedLocId = localStorage.getItem("sellsync_location_id");
    const loc = locs.find((l) => l.id === parseInt(savedLocId)) || locs[0];
    setLocationId(loc?.id || null);
    setLocations(locs);

    const prodData = await authFetch(`/products?limit=${pageSize}&page=${currentPage}`);
    const raw      = prodData.products || [];

    let invMap = {};
    if (loc?.id) {
      try {
        const invData = await authFetch(`/inventory?location_id=${loc.id}`);
        (invData.inventory_levels || []).forEach((l) => {
          invMap[l.inventory_item_id] = l.available ?? 0;
        });
      } catch (_) {}
    }
    setSyncStatus((s) => ({ ...s, shopify:"ok" }));

    let amzInvMap = {};
    if (!skipPlatformSync && tenant?.platforms?.amazon?.connected) {
      try {
        setSyncStatus((s) => ({ ...s, amazon:"syncing" }));
        const amzData = await authFetch("/amazon/inventory");
        (amzData.payload?.inventorySummaries || []).forEach((item) => {
          amzInvMap[item.sellerSku] = item.inventoryDetails?.fulfillableQuantity ?? 0;
        });
        setSyncStatus((s) => ({ ...s, amazon:"ok" }));
      } catch (_) { setSyncStatus((s) => ({ ...s, amazon:"error" })); }
    }

    let fkInvMap = {};
    if (!skipPlatformSync && tenant?.platforms?.flipkart?.connected) {
      try {
        setSyncStatus((s) => ({ ...s, flipkart:"syncing" }));
        const fkData = await authFetch("/flipkart/inventory");
        (fkData.SELLER_SKU_ITEM_MAP || []).forEach((item) => {
          fkInvMap[item.skuId] = item.available || 0;
        });
        setSyncStatus((s) => ({ ...s, flipkart:"ok" }));
      } catch (_) { setSyncStatus((s) => ({ ...s, flipkart:"error" })); }
    }

    const merged = raw.map((p) => {
      const mapped = mapShopifyProduct(p, invMap, manualInv, skuMapLocal);
      const azSku  = skuMapLocal[mapped.shopifySku]?.amazonSku   || "";
      const fkSku  = skuMapLocal[mapped.shopifySku]?.flipkartSku || "";
      return { ...mapped, inventory: { ...mapped.inventory,
        amazon:   azSku && amzInvMap[azSku] !== undefined ? amzInvMap[azSku] : mapped.inventory.amazon,
        flipkart: fkSku && fkInvMap[fkSku]  !== undefined ? fkInvMap[fkSku]  : mapped.inventory.flipkart,
      }};
    });

    setProducts(merged);
    setTotalProducts(prodData.products?.length || merged.length);

    try {
      const o = await authFetch(`/orders?limit=${pageSize}&page=${currentPage}`);
      const shopifyOrders = (o.orders || []).map((ord) => {
        const ful = ord.fulfillments?.[0] || {};
        return {
          ...ord,
          _platform:        "shopify",
          trackingNumber:   ful.tracking_number   || "",
          trackingCompany:  ful.tracking_company  || "",
          trackingUrl:      ful.tracking_url       || (ful.tracking_urls?.[0]) || "",
          trackingNumbers:  ful.tracking_numbers  || (ful.tracking_number ? [ful.tracking_number] : []),
          fulfilledAt:      ful.created_at         || "",
          shipmentStatus:   ful.shipment_status    || "",
        };
      });

      // Fetch Amazon orders if connected
      let amazonOrders = [];
      if (!skipPlatformSync && tenant?.platforms?.amazon?.connected) {
        try {
          const amzO = await authFetch("/amazon/orders");
          amazonOrders = (amzO.payload?.Orders || []).map((ord) => ({
            id:               ord.AmazonOrderId,
            name:             "AMZ-" + ord.AmazonOrderId,
            created_at:       ord.PurchaseDate || new Date().toISOString(),
            financial_status: ord.PaymentMethod === "COD" ? "pending" : "paid",
            fulfillment_status: ord.OrderStatus === "Shipped" ? "fulfilled" :
                                ord.OrderStatus === "Unshipped" ? null : "partial",
            total_price:      ord.OrderTotal?.Amount || 0,
            currency:         ord.OrderTotal?.CurrencyCode || "INR",
            customer: {
              first_name: ord.BuyerInfo?.BuyerName || "Amazon",
              last_name:  "Customer",
              email:      ord.BuyerInfo?.BuyerEmail || "",
            },
            shipping_address: {
              address1: ord.ShippingAddress?.AddressLine1 || "",
              city:     ord.ShippingAddress?.City || "",
              province: ord.ShippingAddress?.StateOrRegion || "",
              zip:      ord.ShippingAddress?.PostalCode || "",
              country:  ord.ShippingAddress?.CountryCode || "IN",
              phone:    ord.ShippingAddress?.Phone || "",
            },
            line_items: [],
            _platform:  "amazon",
            _raw:       ord,
          }));
        } catch (_) {}
      }

      // Merge and sort by date
      const merged = [...shopifyOrders, ...amazonOrders].sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      );
      setOrders(merged);
    } catch (_) {}

    setLoading(false);
    toast(`Synced — ${merged.length} products`, "success");
  } catch (err) {
    setLoading(false);
    toast(err.message, "error");
  }
}, [tenant, toast, currentPage, pageSize]);

// ← ADD THESE TWO AFTER loadAll closing bracket
useEffect(() => {
  if (tenant) loadAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [tenant]);


  // ── update order fulfillment status (Shopify) ───────────────
  const updateOrderStatus = async (orderId, fulfillmentStatus) => {
    try {
      const res = await authFetch("/orders/" + orderId + "/fulfillment", {
        method: "POST",
        body: JSON.stringify({ fulfillment_status: fulfillmentStatus }),
      });
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, fulfillment_status: fulfillmentStatus } : o));
      toast("Order status updated to " + fulfillmentStatus + " ✓", "success");
    } catch(e) {
      toast(e.message, "error");
    }
  };

  // ── print labels for orders ─────────────────────────────────
  const printOrderLabels = (ordersToPrint) => {
    if (!ordersToPrint.length) { toast("No orders to print labels for", "error"); return; }
    // Load SKU database from localStorage (shared with label printer page)
    let skuDatabase = [];
    try {
      const saved = localStorage.getItem("lp_db");
      const map   = localStorage.getItem("lp_colmap");
      if (saved && map) {
        const rows = JSON.parse(saved);
        const colMap = JSON.parse(map);
        const g = (row, f) => colMap[f] != null ? String(row[colMap[f]]||"") : "";
        skuDatabase = rows.map((row) => ({
          sku: g(row,"sku"), asin: g(row,"asin"), name: g(row,"name"),
          mrp: g(row,"mrp"), origin: g(row,"origin")||"India",
          packer: g(row,"packer")||"Vasudeva Enterprises",
          addr: g(row,"addr")||"Tower 4C, Landcraft Golflinks, Pandav Nagar, Ghaziabad UP 201001",
          qtylabel: g(row,"qtylabel")||"1 Piece",
        }));
      }
    } catch {}

    const makeBarcodeUrl = (text) => text
      ? "https://barcode.tec-it.com/barcode.ashx?data="+encodeURIComponent(text)+"&code=Code128&dpi=96&unit=px&imagetype=png&modulewidth=1&height=30&quietzonesize=5"
      : "";

    // Generate all labels
    const labels = [];
    ordersToPrint.forEach((o) => {
      const addr = o.shipping_address || {};
      const cust = o.customer ? o.customer.first_name+" "+o.customer.last_name : addr.name || "Customer";
      const shipTo = [addr.address1, addr.city, addr.province, addr.zip].filter(Boolean).join(", ");
      (o.line_items || []).forEach((item) => {
        const dbRow = skuDatabase.find((r) => r.sku && r.sku.toLowerCase() === (item.sku||"").toLowerCase());
        for (let i = 0; i < (item.quantity||1); i++) {
          labels.push({
            orderId:  o.name,
            platform: "shopify",
            sku:      item.sku || "",
            asin:     dbRow?.asin || "",
            name:     item.name || dbRow?.name || "",
            mrp:      dbRow?.mrp || parseFloat(item.price||0).toFixed(0),
            origin:   dbRow?.origin || "India",
            packer:   dbRow?.packer || "Vasudeva Enterprises",
            addr:     dbRow?.addr || "Tower 4C, Landcraft Golflinks, Pandav Nagar, Ghaziabad UP 201001",
            qtylabel: dbRow?.qtylabel || "1 Piece",
            customer: cust,
            shipTo,
            phone:    addr.phone || "",
          });
        }
      });
    });

    if (!labels.length) { toast("No items found in selected orders", "error"); return; }

    // Build print HTML
    const cells = [];
    for (let i = 0; i < labels.length; i += 6) cells.push(labels.slice(i, i+6));
    const pagesHtml = cells.map((page) => {
      const padded = [...page];
      while (padded.length < 6) padded.push(null);
      return "<div class='page'>" + padded.map((l) => l ? `
        <div class='label'>
          <div style='position:absolute;top:3px;right:3px;background:#5a8a00;color:#fff;font-size:6px;padding:1px 4px;border-radius:3px;font-weight:700'>Shopify</div>
          <div style='display:flex;justify-content:space-between;align-items:flex-start'>
            ${l.sku?`<span style='background:#1A3A5C;color:#fff;border-radius:3px;padding:2px 6px;font-size:7.5px;font-weight:700'><span style='font-size:6px;opacity:.7'>SKU </span>${l.sku}</span>`:"<span></span>"}
            <span style='font-size:12px;font-weight:bold'>₹${l.mrp||""}</span>
          </div>
          <div style='font-size:8.5px;font-weight:bold;text-align:center;line-height:1.2;padding:2px 0;overflow:hidden;max-height:22px'>${l.name||"—"}</div>
          ${l.asin?`<div style='display:flex;justify-content:center'><img src='${makeBarcodeUrl(l.asin)}' style='width:148px;height:30px'></div><div style='font-size:6px;color:#777;text-align:center;font-family:monospace'>${l.asin}</div>`:""}
          <hr style='border:none;border-top:.5px solid #ddd;margin:2px 0'>
          <div style='display:flex;justify-content:space-between'>
            <div><div style='font-size:5.5px;color:#aaa'>Origin</div><div style='font-size:7px;font-weight:600'>${l.origin}</div></div>
            <div><div style='font-size:5.5px;color:#aaa'>Qty</div><div style='font-size:7px;font-weight:600'>${l.qtylabel}</div></div>
          </div>
          <hr style='border:none;border-top:.5px solid #ddd;margin:2px 0'>
          <div style='font-size:7px;font-weight:bold;color:#F47920'>SHIP TO:</div>
          <div style='font-size:6.5px;font-weight:bold;line-height:1.3'>${l.customer}</div>
          <div style='font-size:5.8px;color:#333;line-height:1.3'>${l.shipTo}</div>
          ${l.phone?`<div style='font-size:5.8px;color:#555'>📞 ${l.phone}</div>`:""}
          <div style='font-size:5.5px;color:#aaa'>Order: ${l.orderId}</div>
        </div>` : "<div class='label empty'></div>").join("") + "</div>";
    }).join("");

    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head><title>Labels — ${new Date().toLocaleDateString("en-IN")}</title>
    <style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;background:#fff;}
    .page{width:4in;height:6in;display:grid;grid-template-columns:2in 2in;grid-template-rows:2in 2in 2in;page-break-after:always;break-after:page;}
    .label{width:2in;height:2in;border-right:1.5px dashed #bbb;border-bottom:1.5px dashed #bbb;padding:7px 7px 5px 7px;display:flex;flex-direction:column;gap:2px;overflow:hidden;position:relative;}
    .label:nth-child(2n){border-right:none;}.label:nth-last-child(-n+2){border-bottom:none;}.empty{background:#f8f8f6;}
    @media print{*{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}</style></head><body>${pagesHtml}</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 600);
    toast(`🏷️ ${labels.length} labels ready to print ✓`, "success");
  };

  // ── update inventory ─────────────────────────────────────────
  const updateInv = async (productId, platformId, value) => {
    const qty = Math.max(0, parseInt(value) || 0);
    setProducts((prev) => {
      const updated = prev.map((p) => p.id === productId ? { ...p, inventory: { ...p.inventory, [platformId]: qty } } : p);
      const manual = loadManualInv();
      updated.forEach((p) => { manual[p.id] = { amazon:p.inventory.amazon, flipkart:p.inventory.flipkart, myntra:p.inventory.myntra }; });
      saveManualInv(manual);
      return updated;
    });
    const prod = products.find((p) => p.id === productId);
    if (platformId === "shopify" && prod?.inventoryItemId && locationId) {
      try {
        await authFetch("/inventory/update", { method:"POST", body:JSON.stringify({ inventory_item_id:prod.inventoryItemId, location_id:locationId, available:qty }) });
        toast("Shopify updated ✓", "success");
      } catch { toast("Shopify update failed", "error"); }
    }
    if (platformId === "amazon") {
      const azSku = skuMap[prod?.shopifySku]?.amazonSku;
      if (azSku) { try { await authFetch("/amazon/inventory/update", { method:"POST", body:JSON.stringify({ sku:azSku, quantity:qty }) }); toast("Amazon updated ✓", "success"); } catch { toast("Amazon update failed", "error"); } }
      else toast("Map Amazon SKU first", "error");
    }
    if (platformId === "flipkart") {
      const fkSku = skuMap[prod?.shopifySku]?.flipkartSku;
      if (fkSku) { try { await authFetch("/flipkart/inventory/update", { method:"POST", body:JSON.stringify({ skus:[{ skuId:fkSku, quantity:qty }] }) }); toast("Flipkart updated ✓", "success"); } catch { toast("Flipkart update failed", "error"); } }
      else toast("Map Flipkart SKU first", "error");
    }
  };

  const bulkSet = async (productId, value, el) => {
    const qty = Math.max(0, parseInt(value) || 0);
    for (const pl of PLATFORMS) await updateInv(productId, pl.id, qty);
    if (el) el.value = "";
    toast(`All platforms set to ${qty} ✓`, "success");
  };

  const saveMapping = (shopifySku) => {
    const updated = { ...skuMap, [shopifySku]: { ...(skuMap[shopifySku] || {}), ...(skuEdits[shopifySku] || {}) } };
    setSkuMap(updated); saveSkuMap(updated);
    setSkuEdits((p) => { const n = { ...p }; delete n[shopifySku]; return n; });
    toast("SKU mapping saved ✓", "success");
  };

  const exportMapping = () => {
    const blob = new Blob([JSON.stringify(skuMap, null, 2)], { type:"application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "sku-mapping.json"; a.click();
    toast("Exported ✓", "success");
  };

  const printInvoice = () => {
    if (!invCust.trim()) { toast("Enter customer name", "error"); return; }
    const inv = { id:newInvId(), date:new Date().toLocaleString("en-IN"), productName:invProd?.name, platform:invPlat, qty:invQty, customer:invCust, address:invAddr, total:Math.round((invProd?.price||0)*invQty*1.18) };
    setInvoices((p) => [inv, ...p]);
    if (invProd) updateInv(invProd.id, invPlat, Math.max(0, (invProd.inventory[invPlat]||0) - invQty));
    toast(`Invoice ${inv.id} created ✓`, "success");
    setInvModal(false); setInvCust(""); setInvAddr(""); setInvQty(1); setPage("invoices");
  };

  const filtered = products.filter((p) => { const q = search.toLowerCase(); return !q || p.name.toLowerCase().includes(q) || p.shopifySku.toLowerCase().includes(q); });
  const totalRev = orders.reduce((a, o) => a + parseFloat(o.total_price || 0), 0);
  const lowCount = products.filter((p) => PLATFORMS.some((pl) => p.inventory[pl.id] > 0 && p.inventory[pl.id] <= p.lowAlert)).length;
  const outCount = products.filter((p) => totalStock(p.inventory) === 0).length;
  const statusDot = (st) => ({ idle:{color:"#9ca3af",label:"–"}, syncing:{color:"#f59e0b",label:"Syncing"}, ok:{color:"#22c55e",label:"Live"}, error:{color:"#ef4444",label:"Error"} }[st] || { color:"#9ca3af", label:"–" });

  // ── Time-based dashboard intelligence ─────────────────────────
  const _orderDate = (o) => new Date(o.created_at || o.createdAt || o.processed_at || o.order_date || 0);
  const _startOfToday = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();
  const _startOfWeek  = (() => { const d = new Date(); d.setDate(d.getDate() - 6); d.setHours(0,0,0,0); return d; })();

  const todayOrders = orders.filter((o) => _orderDate(o) >= _startOfToday);
  const todayRevenue = todayOrders.reduce((a, o) => a + parseFloat(o.total_price || 0), 0);

  const weekOrders = orders.filter((o) => _orderDate(o) >= _startOfWeek);
  const weekRevenue = weekOrders.reduce((a, o) => a + parseFloat(o.total_price || 0), 0);

  // Best seller this week (by qty across line items)
  const _skuQty = {};
  weekOrders.forEach((o) => {
    (o.line_items || o.items || []).forEach((li) => {
      const key = li.name || li.title || li.sku || "Item";
      _skuQty[key] = (_skuQty[key] || 0) + (li.quantity || 1);
    });
  });
  const bestSeller = Object.entries(_skuQty).sort((a,b) => b[1]-a[1])[0]; // [name, qty] or undefined

  // Return % this week (orders flagged returned/refunded)
  const _isReturn = (o) => {
    const s = (o.financial_status || o.fulfillment_status || o.status || "").toLowerCase();
    return s.includes("refund") || s.includes("return") || o.cancelled_at || o.isReturn;
  };
  const weekReturns = weekOrders.filter(_isReturn).length;
  const returnPct = weekOrders.length > 0 ? Math.round(weekReturns / weekOrders.length * 1000) / 10 : 0;

  // Rough weekly profit estimate: revenue − est. platform fee (15%) − est. GST (out of inclusive)
  // (Real per-order P&L lives in the Myntra/Marketplace modules; this is a dashboard estimate.)
  const weekProfitEst = Math.round(weekRevenue * 0.55);

  // High-return products this week (by name)
  const _returnByName = {};
  weekOrders.filter(_isReturn).forEach((o) => {
    (o.line_items || o.items || []).forEach((li) => {
      const key = li.name || li.title || li.sku || "Item";
      _returnByName[key] = (_returnByName[key] || 0) + 1;
    });
  });
  const topReturnProduct = Object.entries(_returnByName).sort((a,b) => b[1]-a[1])[0];

  // Pending payouts — orders not yet settled/paid out (COD pending or unfulfilled paid)
  const pendingPayoutCount = orders.filter((o) => {
    const f = (o.financial_status || "").toLowerCase();
    return f === "pending" || f === "authorized" || f === "partially_paid";
  }).length;

  // ── show auth if not logged in ────────────────────────────────
  if (!authReady) return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", fontSize:14, color:"#9ca3af" }}>Loading…</div>;
  if (!tenant)    return <AuthPage onLogin={handleLogin}/>;

  // ── PLATFORM PICKER — first thing the seller does ──
  if (!hasOpted) {
    return <PlatformPicker onConfirm={saveOpted} brandName={tenant.storeName} />;
  }

  const plan = PLANS[tenant.plan] || PLANS.trial;
  const trialDays = tenant.trialEndsAt ? Math.max(0, Math.ceil((new Date(tenant.trialEndsAt) - Date.now()) / 86400000)) : 0;

  // ── invoice modal ─────────────────────────────────────────────
  const InvoiceModal = () => {
    const grand = Math.round((invProd?.price||0)*invQty*1.18);
    return (
      <div style={S.overlay} onClick={(e) => e.target===e.currentTarget && setInvModal(false)}>
        <div style={S.modal}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
            <span style={{ fontWeight:700, fontSize:16 }}>New Invoice</span>
            <button onClick={() => setInvModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>✕</button>
          </div>
          <label style={S.fLabel}>Product</label>
          <select style={S.fInput} value={invProd?.id||""} onChange={(e) => setInvProd(products.find((p) => p.id===e.target.value))}>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <label style={S.fLabel}>Channel</label>
          <div style={{ display:"flex", gap:8, marginBottom:12 }}>
            {myPlatforms.map((pl) => (
              <button key={pl.id} onClick={() => setInvPlat(pl.id)} style={{ flex:1, padding:"6px 4px", borderRadius:7, fontSize:11, cursor:"pointer", fontFamily:"inherit", fontWeight:600, border:`1px solid ${pl.color}`, background:invPlat===pl.id?pl.color:pl.bg, color:invPlat===pl.id?"#fff":pl.color }}>{pl.name}</button>
            ))}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div><label style={S.fLabel}>Customer Name</label><input style={S.fInput} placeholder="e.g. Ravi Kumar" value={invCust} onChange={(e) => setInvCust(e.target.value)}/></div>
            <div><label style={S.fLabel}>Quantity</label><input style={S.fInput} type="number" min="1" value={invQty} onChange={(e) => setInvQty(parseInt(e.target.value)||1)}/></div>
          </div>
          <label style={S.fLabel}>Delivery Address</label>
          <input style={S.fInput} placeholder="Full address" value={invAddr} onChange={(e) => setInvAddr(e.target.value)}/>
          {invProd && (
            <div style={{ background:"#f9fafb", borderRadius:8, padding:14, marginBottom:14 }}>
              {[["Unit Price",INR(invProd.price)],["Qty",invQty],["GST 18%",INR(Math.round(invProd.price*invQty*0.18))]].map(([l,v]) => (
                <div key={l} style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:6, color:"#6b7280" }}><span>{l}</span><span style={{ color:"#111" }}>{v}</span></div>
              ))}
              <div style={{ display:"flex", justifyContent:"space-between", fontWeight:700, fontSize:15, borderTop:"1px solid #e5e7eb", paddingTop:10 }}>
                <span>Grand Total</span><span style={{ color:"#F47920" }}>{INR(grand)}</span>
              </div>
            </div>
          )}
          <button style={{ ...S.btn(), width:"100%", padding:"10px", fontSize:14 }} onClick={printInvoice}>🖨️ Print Invoice</button>
        </div>
      </div>
    );
  };

  return (
    <div style={S.wrap}>
      {/* toasts */}
      <div style={{ position:"fixed", top:16, right:16, zIndex:9999, width:300, display:"flex", flexDirection:"column", gap:8 }}>
        {toasts.map((t) => (
          <div key={t.id} style={{ padding:"10px 14px", borderRadius:8, fontSize:13, fontWeight:500, background:t.type==="success"?"#f0fdf4":t.type==="error"?"#fef2f2":"#fffbeb", color:t.type==="success"?"#15803d":t.type==="error"?"#b91c1c":"#92400e", border:`1px solid ${t.type==="success"?"#bbf7d0":t.type==="error"?"#fecaca":"#fde68a"}`, boxShadow:"0 2px 8px rgba(0,0,0,0.08)" }}>{t.msg}</div>
        ))}
      </div>
      {invModal && <InvoiceModal/>}

      {/* ── FULL WIDTH LAYOUT WITH TOP NAV ── */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden", minWidth:0 }}>
        {/* Top header bar */}
        <div style={{ background:"#fff", borderBottom:"1px solid #e5e7eb", padding:"0 20px", display:"flex", alignItems:"center", gap:16, height:52, flexShrink:0, zIndex:100 }}>
          {/* Logo */}
          <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0, cursor:"pointer" }} onClick={()=>setPage("dashboard")}>
            <div style={{ width:28, height:28, borderRadius:6, background:"#F47920", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:14 }}>3</div>
            <div style={{ fontWeight:700, fontSize:14, color:"#1A3A5C" }}>3June</div>
          </div>
          <div style={{ width:1, height:28, background:"#e5e7eb", flexShrink:0 }}/>
          {/* Module tabs */}
          <div style={{ display:"flex", gap:2, flex:1, overflowX:"auto" }}>
            {[
              { id:"dashboard",   label:"🏠 Dashboard",   pages:["getstarted","dashboard","calculator"] },
              { id:"orders",      label:"🛒 Orders",       pages:["orders","invoicepdf"] },
              { id:"inventory",   label:"📦 Inventory",    pages:["inventory","wms"] },
              { id:"procurement", label:"📋 Procurement",  pages:["purchases","returns","sourcing"] },
              { id:"catalog",     label:"🗂️ Catalog",      pages:["pim","skumap","myntraImages"] },
              { id:"channels",    label:"🌐 Channels",     pages:["channels","logistics"] },
              { id:"finance",     label:"💰 Finance",      pages:["gst","expenses","accounting","reconciliation","reports"] },
              { id:"operations",  label:"⚡ Operations",   pages:["automation","tasks"] },
              { id:"settings",    label:"⚙️ Settings",     pages:["settings","team","billing","whatsapp","profile"] },
            ].filter((mod) => effectiveMode === "advanced" || STARTER_MODULES.includes(mod.id)).map((mod) => (
              <button key={mod.id}
                onClick={() => { setActiveModule(mod.id); setPage(mod.pages[0]); }}
                style={{ padding:"0 12px", height:52, border:"none", borderBottom:activeModule===mod.id?"2px solid #F47920":"2px solid transparent", background:"none", color:activeModule===mod.id?"#F47920":"#6b7280", fontWeight:activeModule===mod.id?600:400, fontSize:12, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", flexShrink:0 }}>
                {mod.label}
              </button>
            ))}
          </div>
          {/* Right side — mode toggle + search + user */}
          <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
            {/* Starter / Advanced mode toggle — paid users only */}
            <button onClick={()=>{ if(window.confirm("Change which platforms you sell on?")) { localStorage.removeItem("3june_opted_platforms"); window.location.reload(); } }}
              title="Change your selling platforms"
              style={{ background:"#f3f4f6", border:"1px solid #e5e7eb", borderRadius:8, padding:"5px 10px", fontSize:11, fontFamily:"inherit", fontWeight:600, color:"#6b7280", cursor:"pointer" }}>
              🛒 {optedPlatforms?.length || "All"} platforms
            </button>
            {lockedToStarter ? (
              <button onClick={()=>{ setActiveModule("settings"); setPage("billing"); }}
                title="Upgrade to unlock Advanced features"
                style={{ display:"flex", alignItems:"center", gap:5, background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:8, padding:"5px 12px", fontSize:11, fontFamily:"inherit", fontWeight:600, color:"#F47920", cursor:"pointer" }}>
                🔒 Advanced — Upgrade
              </button>
            ) : (
              <div style={{ display:"flex", background:"#f3f4f6", borderRadius:8, padding:2, fontSize:11, fontFamily:"inherit" }} title="Starter shows core tools. Advanced shows everything.">
                <button onClick={()=>setAppMode("starter")}
                  style={{ padding:"4px 10px", borderRadius:6, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:viewMode==="starter"?700:400,
                    background: viewMode==="starter"?"#fff":"transparent", color: viewMode==="starter"?"#F47920":"#9ca3af",
                    boxShadow: viewMode==="starter"?"0 1px 3px rgba(0,0,0,0.1)":"none" }}>
                  Starter
                </button>
                <button onClick={()=>setAppMode("advanced")}
                  style={{ padding:"4px 10px", borderRadius:6, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:viewMode==="advanced"?700:400,
                    background: viewMode==="advanced"?"#fff":"transparent", color: viewMode==="advanced"?"#F47920":"#9ca3af",
                    boxShadow: viewMode==="advanced"?"0 1px 3px rgba(0,0,0,0.1)":"none" }}>
                  Advanced
                </button>
              </div>
            )}
            <input placeholder="Search SKU or product…" style={{ padding:"5px 10px", borderRadius:7, border:"1px solid #e5e7eb", fontSize:12, width:160, fontFamily:"inherit" }}
              value={search} onChange={(e)=>setSearch(e.target.value)}/>
            <button onClick={loadAll} style={{ padding:"5px 10px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#f3f4f6", border:"none", fontFamily:"inherit", color:"#374151" }}>↻ Sync</button>
            <div style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer" }} onClick={()=>setPage("profile")}>
              <div style={{ width:28, height:28, borderRadius:"50%", background:"#F47920", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:12 }}>
                {tenant?.storeName?.charAt(0)?.toUpperCase() || "V"}
              </div>
              <span style={{ fontSize:12, fontWeight:500, color:"#374151", maxWidth:80, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{tenant?.storeName}</span>
            </div>
          </div>
        </div>

        {/* Sub-navigation (second row) — shows pages within active module */}
        {true && (
          <div style={{ background:"#f9fafb", borderBottom:"1px solid #e5e7eb", padding:"0 20px", display:"flex", gap:4, height:38, alignItems:"center", flexShrink:0, overflowX:"auto" }}>
            {(() => {
              const allSub = {
                dashboard:   [["getstarted","🚀 Get Started"],["dashboard","📊 Dashboard"],["calculator","🎯 ROAS Calculator"]],
                orders:      [["orderspro","🛒 Orders Pro"],["orders","📋 All Orders"],["invoicepdf","🧾 Invoices"]],
                inventory:   [["inventorypro","📦 Inventory Pro"],["inventory","📊 Inventory"],["wms","🏭 Warehouse"]],
                procurement: [["purchases","📋 Purchase Orders"],["sourcing","🏭 Sourcing"],["returns","↩️ Returns"],["customerpro","👥 Customers"]],
                catalog:     [["catalogpro","🗂️ Catalog Pro"],["pim","📦 Legacy"],["skumap","🔗 SKU Map"],["myntraImages","🖼️ Images"]],
                channels:    [["channels","🌐 Platforms"],["myntraimport","🛍 Myntra Reports"],["myntrapnl","📊 Myntra P&L"],["marketplaceimport","🌐 Marketplace Reports"],["skumapping","🔗 SKU Mapping"],["logistics","🚚 Logistics"]],
                finance:     [["financepro","💰 Finance Pro"],["gst","📊 GST"],["expenses","💸 Expenses"],["reconciliation","🔍 Reconciliation"],["reports","📈 Reports"],["accounting","📤 Tally"]],
                operations:  [["taskspro","⚡ Ops Pro"],["labels","🏷️ Labels"],["automation","⚡ Automation"],["tasks","✅ Tasks"]],
                settings:    [["getstarted","🚀 Get Started"],["modules","🧩 Modules"],["settings","⚙️ Settings"],["team","👥 Team"],["billing","💳 Billing"],["profile","👤 Profile"]],
              };
              // In Starter mode, show only the essential sub-page(s) per module
              const starterSub = {
                dashboard:   [["getstarted","🚀 Get Started"],["dashboard","📊 Dashboard"],["calculator","🎯 ROAS Calculator"]],
                orders:      [["orders","📋 All Orders"],["invoicepdf","🧾 Invoices"]],
                inventory:   [["inventory","📊 Inventory"]],
                finance:     [["gst","📊 GST"]],
                settings:    [["settings","⚙️ Settings"],["billing","💳 Billing"],["profile","👤 Profile"]],
              };
              const src = effectiveMode === "advanced"
                ? allSub[activeModule]
                : (starterSub[activeModule] || allSub[activeModule]);
              return (src || []);
            })().map(([id,label]) => (
              <button key={id} onClick={()=>setPage(id)}
                style={{ padding:"0 12px", height:38, border:"none", borderBottom:page===id?"2px solid #1A3A5C":"2px solid transparent", background:"none", color:page===id?"#1A3A5C":"#6b7280", fontWeight:page===id?600:400, fontSize:12, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Page content */}
        <div style={{ display:"none" }}>
          <button onClick={() => {}} style={{ display:"none" }} className="show-mobile">☰</button>
          <div style={{ flex:1, fontWeight:600, fontSize:15 }}>{
              {
                dashboard:"Dashboard", inventory:"Inventory", orders:"Orders",
                purchases:"Purchase Orders", returns:"Returns",
                channels:"Platforms", skumap:"SKU Mapping", logistics:"Logistics", 
                 gst:"GST & Analytics", settings:"Settings", billing:"Billing & Plans", invoicepdf: "Invoice Generator", wms: "Warehouse Management", pim: "Product Catalog (PIM)", accounting: "Accounting Export", whatsapp: "WhatsApp Notifications", reconciliation: "Financial Reconciliation", automation: "Automation Engine", reports: "Reports & Insights", team: "Team & Roles", profile: "My Profile", labels: "Label Printer",
              }[page] || "Dashboard"
          }</div>
          
          <WarehouseSelector
            locations={locations}
            locationId={locationId}
            onSelect={(id) => { setLocationId(id); loadAll(); }}
          />

          <input style={{ ...S.input, width:220 }} placeholder="Search SKU or product…" value={search} onChange={(e) => setSearch(e.target.value)}/>
          <button style={S.btnOut} onClick={() => { setSyncing(true); loadAll().finally(() => setSyncing(false)); }}>
            {syncing ? "Syncing…" : "↻ Sync All"}
          </button>
          <button style={S.btn()} onClick={() => { setInvProd(products[0]); setInvModal(true); }}>+ Invoice</button>
        </div>

        <div style={S.content} className="sellsync-content table-wrap">
          {loading ? (
            <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
              <div style={{ fontSize:32, marginBottom:12 }}>⏳</div>Connecting to platforms…
            </div>
          ) : (
            <>
              {/* DEMO BANNER */}
              {tenant?.isDemo && (
                <div style={{ background:"linear-gradient(90deg,#1A3A5C,#2563eb)", color:"#fff", padding:"8px 16px", marginBottom:12, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:12 }}>
                  <span>🎭 <strong>Demo Account</strong> — Sample Leather Patta data · All modules unlocked</span>
                  <span style={{ opacity:0.7, fontSize:11 }}>demo@3june.in / Demo@3june1</span>
                </div>
              )}
              {/* ── FREE-PLAN UPGRADE GATE ── */}
              {isLockedForPlan(tenant?.plan, page) ? (
                <div style={{ maxWidth:560, margin:"40px auto", textAlign:"center", background:"#fff", border:"1px solid #e5e7eb", borderRadius:16, padding:"48px 40px" }}>
                  <div style={{ fontSize:52, marginBottom:16 }}>🔒</div>
                  <div style={{ fontSize:24, fontWeight:800, color:"#0F1A0F", marginBottom:10 }}>
                    Upgrade to unlock {({orders:"Orders",inventory:"Inventory",finance:"Finance",channels:"Channels",procurement:"Procurement",catalog:"Catalog",operations:"Operations"}[activeModule]) || "this feature"}
                  </div>
                  <div style={{ fontSize:14, color:"#6b7280", lineHeight:1.7, marginBottom:28, maxWidth:420, marginLeft:"auto", marginRight:"auto" }}>
                    You're on the <strong>Free plan</strong> — the ROAS Calculator is yours forever, free. To sync orders, track inventory, see per-order profit and file GST, upgrade to a paid plan.
                  </div>
                  <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:12, padding:"18px 22px", marginBottom:28, textAlign:"left" }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#92670a", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:10 }}>Paid plans unlock</div>
                    {["Per-order profit & loss across all platforms","Order & inventory sync (Shopify, Myntra, Amazon…)","GST reports + Tally export","Payment reconciliation — catch money you're owed"].map((f,i)=>(
                      <div key={i} style={{ fontSize:13, color:"#3d3020", padding:"4px 0", display:"flex", gap:8 }}><span style={{ color:"#16a34a" }}>✓</span>{f}</div>
                    ))}
                  </div>
                  <button onClick={()=>{ setActiveModule("settings"); setPage("billing"); }} style={{ background:"#F47920", color:"#fff", border:"none", borderRadius:10, padding:"13px 32px", fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"inherit", boxShadow:"0 4px 14px rgba(244,121,32,0.3)" }}>
                    See plans &amp; upgrade →
                  </button>
                  <div style={{ marginTop:18 }}>
                    <button onClick={()=>{ setActiveModule("dashboard"); setPage("calculator"); }} style={{ background:"none", border:"none", color:"#6b7280", fontSize:13, cursor:"pointer", fontFamily:"inherit", textDecoration:"underline" }}>
                      ← Back to the free ROAS Calculator
                    </button>
                  </div>
                </div>
              ) : (
              <>
              {/* DASHBOARD */}
              {page==="calculator" && <PageCalculator toast={toast} products={products} tenant={tenant} onUpgrade={()=>{ setActiveModule("settings"); setPage("billing"); }}/>}
              {page==="orderspro"   && <PageOrdersPro   toast={toast} orders={orders} products={products} tenant={tenant} onSync={loadAll}/>}
              {page==="inventorypro" && <PageInventoryPro toast={toast} orders={orders} products={products} tenant={tenant}/>}
              {page==="financepro"   && <PageFinancePro   toast={toast} orders={orders} products={products} tenant={tenant}/>}
              {page==="catalogpro"   && <PageCatalogPro   toast={toast} products={products} tenant={tenant}/>}
              {page==="customerpro"  && <PageCustomerPro  toast={toast} orders={orders} products={products} tenant={tenant}/>}
              {page==="taskspro"     && <PageTasksPro     toast={toast} orders={orders} products={products} tenant={tenant}/>}
              {page==="getstarted"   && <PageGetStarted toast={toast} tenant={tenant} products={products} orders={orders} onNavigate={(pg)=>{ const mm={inventorypro:"inventory",orderspro:"orders",financepro:"finance",catalogpro:"catalog",customerpro:"procurement",taskspro:"operations",labels:"operations",channels:"channels",team:"settings",modules:"settings",profile:"settings"}; setActiveModule(mm[pg]||"dashboard"); setPage(pg); }}/>}
              {page==="modules" && <PageModules toast={toast} tenant={tenant} onModulesUpdate={setActiveModules}/>}
              {page==="dashboard" && (
                <>
                  {/* ── TODAY ── */}
                  <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"1px", margin:"2px 0 8px 2px" }}>📅 Today</div>
                  <div className="kpi-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:16 }}>
                    {[
                      { label:"Orders Received", value:todayOrders.length,    color:"#2563eb", sub:todayOrders.length===0?"none yet today":"since midnight", icon:"🛒" },
                      { label:"Revenue Today",   value:INR(todayRevenue),     color:"#F47920", sub:todayOrders.length+" orders",        icon:"📈" },
                      { label:"Low Stock",       value:lowCount,              color:"#d97706", sub:outCount+" out of stock",            icon:"⚠️", click:()=>{ setActiveModule("inventory"); setPage("inventory"); } },
                    ].map(k=>(
                      <div key={k.label} onClick={k.click} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px", borderLeft:"3px solid "+k.color, display:"flex", alignItems:"center", gap:12, cursor:k.click?"pointer":"default" }}>
                        <span style={{ fontSize:24 }}>{k.icon}</span>
                        <div>
                          <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.4px" }}>{k.label}</div>
                          <div style={{ fontSize:24, fontWeight:700, color:k.color }}>{k.value}</div>
                          <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{k.sub}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ── THIS WEEK ── */}
                  <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:"1px", margin:"2px 0 8px 2px" }}>📊 This Week (last 7 days)</div>
                  <div className="kpi-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:16 }}>
                    {[
                      { label:"Best Seller",   value:bestSeller?bestSeller[0].slice(0,18):"—", color:"#15803d", sub:bestSeller?bestSeller[1]+" units sold":"no sales yet", icon:"🏆", big:false },
                      { label:"Return Rate",   value:returnPct+"%",          color:returnPct>15?"#dc2626":"#7e22ce", sub:weekReturns+" of "+weekOrders.length+" orders", icon:"↩️" },
                      { label:"Est. Profit",   value:INR(weekProfitEst),     color:"#F47920", sub:"on "+INR(weekRevenue)+" revenue", icon:"💰" },
                    ].map(k=>(
                      <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px", borderLeft:"3px solid "+k.color, display:"flex", alignItems:"center", gap:12 }}>
                        <span style={{ fontSize:24 }}>{k.icon}</span>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.4px" }}>{k.label}</div>
                          <div style={{ fontSize:k.big===false?16:24, fontWeight:700, color:k.color, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{k.value}</div>
                          <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{k.sub}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* ── MAIN GRID ── */}
                  <div className="dash-main-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 280px", gap:12, marginBottom:12 }}>

                    {/* Platform stock */}
                    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                      <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", justifyContent:"space-between" }}>
                        <span>📊 Platform Stock</span>
                        <button style={{ background:"none", border:"none", fontSize:11, color:"#F47920", cursor:"pointer" }} onClick={loadAll}>↻ Sync</button>
                      </div>
                      <div style={{ padding:14 }}>
                        {PLATFORMS.map(pl=>{
                          const stock = products.reduce((a,p)=>a+(p.inventory[pl.id]||0),0);
                          const maxStock = Math.max(...PLATFORMS.map(p2=>products.reduce((a,p)=>a+(p.inventory[p2.id]||0),0)),1);
                          const dot = syncStatus[pl.id];
                          const statusColor = dot==="ok"?"#15803d":dot==="syncing"?"#d97706":dot==="error"?"#dc2626":"#9ca3af";
                          const statusLabel = dot==="ok"?"Live":dot==="syncing"?"Syncing":dot==="error"?"Error":"Idle";
                          return (
                            <div key={pl.id} style={{ marginBottom:12 }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                                <span style={{ fontWeight:600, fontSize:12, color:pl.color }}>{pl.name}</span>
                                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                  <span style={{ fontSize:12, fontWeight:700 }}>{stock.toLocaleString("en-IN")}</span>
                                  <span style={{ fontSize:9, color:statusColor, fontWeight:600 }}>● {statusLabel}</span>
                                </div>
                              </div>
                              <div style={{ height:5, background:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
                                <div style={{ height:5, background:pl.color, width:Math.min(100,stock/maxStock*100)+"%", borderRadius:3, transition:"width .4s" }}/>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Recent orders */}
                    <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                      <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", justifyContent:"space-between" }}>
                        <span>🛒 Recent Orders</span>
                        <button style={{ background:"none", border:"none", fontSize:11, color:"#F47920", cursor:"pointer" }} onClick={()=>{ setActiveModule("orders"); setPage("orderspro"); }}>View all →</button>
                      </div>
                      <div>
                        {orders.slice(0,6).map((o,i)=>{
                          const name = ((o.customer?.first_name||"")+" "+(o.customer?.last_name||"")).trim()||"Guest";
                          const city = o.shipping_address?.city||"";
                          const isCOD= o.financial_status==="pending";
                          const plat = o._platform||"shopify";
                          const platColors={shopify:"#5a8a00",amazon:"#c45000",flipkart:"#1b57c0",myntra:"#b5003c",meesho:"#9f2089"};
                          return (
                            <div key={o.id||i} style={{ padding:"9px 16px", borderBottom:"1px solid #f9fafb", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                              <div>
                                <div style={{ fontWeight:600, fontSize:12 }}>{name}</div>
                                <div style={{ fontSize:10, color:"#9ca3af" }}>{o.name||o.id} · {city}</div>
                              </div>
                              <div style={{ textAlign:"right" }}>
                                <div style={{ fontWeight:600, fontSize:12 }}>{INR(o.total_price||0)}</div>
                                <span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, background:isCOD?"#fdf4ff":o.fulfillment_status==="fulfilled"?"#f0fdf4":"#fff8f2", color:isCOD?"#7e22ce":o.fulfillment_status==="fulfilled"?"#15803d":"#F47920", fontWeight:700 }}>
                                  {isCOD?"COD":o.fulfillment_status==="fulfilled"?"Done":"Pending"}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                        {orders.length===0 && <div style={{ padding:30, textAlign:"center", color:"#9ca3af", fontSize:12 }}>No orders yet — sync your platforms</div>}
                      </div>
                    </div>

                    {/* Right column */}
                    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                      {/* Alerts */}
                      <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                        <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>🔔 Alerts</div>
                        <div style={{ padding:"4px 0" }}>
                          {outCount>0 && <div style={{ padding:"8px 16px", display:"flex", gap:8, alignItems:"flex-start", borderBottom:"1px solid #f9fafb" }}>
                            <div style={{ width:7,height:7,borderRadius:"50%",background:"#dc2626",marginTop:4,flexShrink:0 }}/>
                            <div>
                              <div style={{ fontSize:12,fontWeight:500 }}>{outCount} products out of stock</div>
                              <div style={{ fontSize:10,color:"#9ca3af" }}>Restock immediately</div>
                            </div>
                          </div>}
                          {lowCount>0 && <div style={{ padding:"8px 16px", display:"flex", gap:8, alignItems:"flex-start", borderBottom:"1px solid #f9fafb" }}>
                            <div style={{ width:7,height:7,borderRadius:"50%",background:"#d97706",marginTop:4,flexShrink:0 }}/>
                            <div>
                              <div style={{ fontSize:12,fontWeight:500 }}>{lowCount} products low stock</div>
                              <div style={{ fontSize:10,color:"#9ca3af" }}>Check inventory</div>
                            </div>
                          </div>}
                          {orders.filter(o=>!o.fulfillment_status).length>0 && <div style={{ padding:"8px 16px", display:"flex", gap:8, alignItems:"flex-start", borderBottom:"1px solid #f9fafb" }}>
                            <div style={{ width:7,height:7,borderRadius:"50%",background:"#F47920",marginTop:4,flexShrink:0 }}/>
                            <div>
                              <div style={{ fontSize:12,fontWeight:500 }}>{orders.filter(o=>!o.fulfillment_status).length} orders pending</div>
                              <div style={{ fontSize:10,color:"#9ca3af" }}>Need fulfillment</div>
                            </div>
                          </div>}
                          {topReturnProduct && topReturnProduct[1]>=2 && <div style={{ padding:"8px 16px", display:"flex", gap:8, alignItems:"flex-start", borderBottom:"1px solid #f9fafb" }}>
                            <div style={{ width:7,height:7,borderRadius:"50%",background:"#dc2626",marginTop:4,flexShrink:0 }}/>
                            <div>
                              <div style={{ fontSize:12,fontWeight:500 }}>High returns: {topReturnProduct[0].slice(0,22)}</div>
                              <div style={{ fontSize:10,color:"#9ca3af" }}>{topReturnProduct[1]} returns this week — check sizing/quality</div>
                            </div>
                          </div>}
                          {pendingPayoutCount>0 && <div style={{ padding:"8px 16px", display:"flex", gap:8, alignItems:"flex-start", borderBottom:"1px solid #f9fafb" }}>
                            <div style={{ width:7,height:7,borderRadius:"50%",background:"#2563eb",marginTop:4,flexShrink:0 }}/>
                            <div>
                              <div style={{ fontSize:12,fontWeight:500 }}>{pendingPayoutCount} pending payouts</div>
                              <div style={{ fontSize:10,color:"#9ca3af" }}>Payment not yet settled</div>
                            </div>
                          </div>}
                          {outCount===0&&lowCount===0&&orders.filter(o=>!o.fulfillment_status).length===0&&!(topReturnProduct&&topReturnProduct[1]>=2)&&pendingPayoutCount===0 && <div style={{ padding:"16px", fontSize:12, color:"#15803d", textAlign:"center" }}>✅ All clear!</div>}
                        </div>
                      </div>

                      {/* Quick access */}
                      <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                        <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>⚡ Quick Access</div>
                        <div className="quick-access-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, padding:12 }}>
                          {[
                            { icon:"📦", label:"Inventory",  page:"inventorypro" },
                            { icon:"🛒", label:"Orders",     page:"orderspro" },
                            { icon:"💰", label:"Finance",    page:"financepro" },
                            { icon:"🗂️", label:"Catalog",    page:"catalogpro" },
                            { icon:"👥", label:"Customers",  page:"customerpro" },
                            { icon:"✅", label:"Tasks",      page:"taskspro" },
                          ].map(m=>(
                            <button key={m.page} onClick={()=>{ const modMap={inventorypro:"inventory",orderspro:"orders",financepro:"finance",catalogpro:"catalog",customerpro:"procurement",taskspro:"operations"}; setActiveModule(modMap[m.page]||"dashboard"); setPage(m.page); }} style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, padding:"8px", cursor:"pointer", fontFamily:"inherit", textAlign:"center", fontSize:11, color:"#374151", fontWeight:500 }}>
                              <div style={{ fontSize:18, marginBottom:2 }}>{m.icon}</div>
                              {m.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Tasks urgent */}
                      <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                        <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", justifyContent:"space-between" }}>
                          <span>✅ Urgent Tasks</span>
                          <button style={{ background:"none",border:"none",fontSize:11,color:"#F47920",cursor:"pointer" }} onClick={()=>{ setActiveModule("operations"); setPage("taskspro"); }}>View all →</button>
                        </div>
                        <div style={{ padding:"4px 0" }}>
                          {urgentTasks.slice(0,4).map((t,i)=>(
                            <div key={t._id||i} style={{ padding:"7px 16px", borderBottom:"1px solid #f9fafb", display:"flex", alignItems:"center", gap:10 }}>
                              <div style={{ width:7,height:7,borderRadius:"50%",background:"#dc2626",flexShrink:0 }}/>
                              <div style={{ fontSize:12, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.title}</div>
                              {t.dueDate && <div style={{ fontSize:10,color:"#9ca3af",flexShrink:0 }}>{new Date(t.dueDate).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}</div>}
                            </div>
                          ))}
                          {urgentTasks.length===0 && <div style={{ padding:"12px 16px",fontSize:12,color:"#15803d" }}>✅ No urgent tasks</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  
                </>
              )}
              {/* INVENTORY */}
              {page==="inventory" && (
                <div style={S.card}>
                  <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                    <input style={{ ...S.input, width:240 }} placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)}/>
                    <span style={{ fontSize:11, color:"#9ca3af" }}>{filtered.length} products</span>
                    <button style={{ ...S.btn("#2563eb"), marginLeft:"auto" }} onClick={loadAll}>↻ Refresh</button>
                  </div>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead><tr>{["Product","SKU",...myPlatforms.map((p)=>p.name),"Total","Set All","Status","Price",""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {filtered.map((product) => {
                          const st = stockStatus(product.inventory, product.lowAlert);
                          return (
                            <tr key={product.id} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                              <td style={S.td}>
                                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                  {product.imageUrl ? <img src={product.imageUrl} alt="" style={{ width:30, height:30, borderRadius:6, objectFit:"cover" }}/> : <div style={{ width:30, height:30, borderRadius:6, background:"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>📦</div>}
                                  <span style={{ fontWeight:500, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{product.name}</span>
                                </div>
                              </td>
                              <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#6b7280" }}>{product.shopifySku}</td>
                              {myPlatforms.map((pl) => {
                                const qty = product.inventory[pl.id] ?? 0;
                                return <td key={pl.id} style={S.td}><input type="number" min="0" style={S.qtyInp(qty,product.lowAlert)} defaultValue={qty} onBlur={(e) => updateInv(product.id,pl.id,e.target.value)} onKeyDown={(e) => { if(e.key==="Enter") e.target.blur(); }}/></td>;
                              })}
                              <td style={{ ...S.td, fontWeight:700, color:"#F47920" }}>{totalStock(product.inventory)}</td>
                              <td style={S.td}><input type="number" min="0" placeholder="All" style={{ ...S.input, width:54, textAlign:"center" }} onKeyDown={(e) => { if(e.key==="Enter"&&e.target.value) bulkSet(product.id,e.target.value,e.target); }} onBlur={(e) => { if(e.target.value) bulkSet(product.id,e.target.value,e.target); }}/></td>
                              <td style={S.td}><Badge {...st}/></td>
                              <td style={S.td}>{INR(product.price)}</td>
                              <td style={S.td}><button style={S.btnOut} onClick={() => { setInvProd(product); setInvModal(true); }}>Invoice</button></td>
                            </tr>
                          );
                        })}
                        {filtered.length===0 && <tr><td colSpan={10} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:40 }}>No products found</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    currentPage={currentPage}
                    totalItems={totalProducts}
                    pageSize={pageSize}
                    onPageChange={(p) => { setCurrentPage(p); loadAll(); }}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); loadAll(); }}
                  />
                </div>
              )}

              {/* SKU MAP */}
              {page==="skumap" && (
                <>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                    <div style={{ fontSize:13, color:"#6b7280" }}>Map Shopify SKUs to Amazon, Flipkart and Myntra SKUs.</div>
                    <button style={S.btn("#2563eb")} onClick={exportMapping}>⬇ Export JSON</button>
                  </div>
                  <div style={S.card}>
                    <div style={{ overflowX:"auto" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse" }}>
                        <thead><tr>{["Product","Shopify SKU","Amazon SKU","Flipkart SKU","Myntra SKU",""].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {products.map((p) => {
                            const edit = skuEdits[p.shopifySku] || {};
                            const map  = skuMap[p.shopifySku]   || {};
                            const isDirty = Object.keys(edit).length > 0;
                            return (
                              <tr key={p.id} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                                <td style={S.td}>
                                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                    {p.imageUrl ? <img src={p.imageUrl} alt="" style={{ width:28, height:28, borderRadius:5, objectFit:"cover" }}/> : <div style={{ width:28, height:28, borderRadius:5, background:"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>📦</div>}
                                    <span style={{ fontWeight:500 }}>{p.name}</span>
                                  </div>
                                </td>
                                <td style={S.td}><span style={{ fontFamily:"monospace", fontSize:12, background:"#f3f4f6", padding:"2px 8px", borderRadius:5 }}>{p.shopifySku}</span></td>
                                {["amazonSku","flipkartSku","myntraSku"].map((field) => (
                                  <td key={field} style={S.td}>
                                    <input style={{ ...S.input, width:140 }} placeholder="Enter SKU" value={edit[field]!==undefined?edit[field]:(map[field]||"")} onChange={(e) => setSkuEdits((prev) => ({ ...prev, [p.shopifySku]:{ ...(prev[p.shopifySku]||{}), [field]:e.target.value } }))}/>
                                  </td>
                                ))}
                                <td style={S.td}><button style={S.btn(isDirty?"#F47920":"#9ca3af")} onClick={() => saveMapping(p.shopifySku)}>{isDirty?"Save":"Saved ✓"}</button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {/* ORDERS */}
              {page==="orders" && (
                <div style={S.card}>
                  <div style={S.cardHdr}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span>Orders ({orders.length})</span>
                      <Badge label="Shopify" color="#5a8a00" bg="#f3f9ec"/>
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button style={{ padding:"5px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#1A3A5C", color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }}
                        onClick={() => {
                          const today = new Date(); today.setHours(0,0,0,0);
                          const todayOrders = orders.filter((o) => new Date(o.created_at) >= today && o.fulfillment_status !== "fulfilled");
                          if (!todayOrders.length) { toast("No unfulfilled orders today", "error"); return; }
                          printOrderLabels(todayOrders);
                        }}>
                        🏷️ Today's Labels ({orders.filter((o) => { const t=new Date(); t.setHours(0,0,0,0); return new Date(o.created_at)>=t && o.fulfillment_status!=="fulfilled"; }).length})
                      </button>
                      <button style={{ padding:"5px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#6366f1", color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }}
                        onClick={() => {
                          const unfulfilled = orders.filter((o) => o.fulfillment_status !== "fulfilled");
                          if (!unfulfilled.length) { toast("No unfulfilled orders", "error"); return; }
                          printOrderLabels(unfulfilled);
                        }}>
                        🏷️ All Pending Labels ({orders.filter((o) => o.fulfillment_status !== "fulfilled").length})
                      </button>
                    </div>
                  </div>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse" }}>
                      <thead><tr>{["Order","Date","Customer","Items","Total","Payment","Fulfillment","Actions"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {orders.map((o) => (
                          <tr key={o.id} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                            <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#6b7280" }}>{o.name}</td>
                            <td style={{ ...S.td, fontSize:11 }}>{new Date(o.created_at).toLocaleDateString("en-IN")}</td>
                            <td style={{ ...S.td, fontWeight:500 }}>{o.customer?`${o.customer.first_name} ${o.customer.last_name}`:"Guest"}</td>
                            <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{o.line_items?.slice(0,2).map((l) => `${l.name}×${l.quantity}`).join(", ")}</td>
                            <td style={{ ...S.td, fontWeight:600 }}>{INR(o.total_price)}</td>
                            <td style={S.td}><Badge label={o.financial_status||"–"} color={o.financial_status==="paid"?"#15803d":"#b45309"} bg={o.financial_status==="paid"?"#f0fdf4":"#fffbeb"}/></td>
                            <td style={S.td}>
                              <select
                                value={o.fulfillment_status || "unfulfilled"}
                                onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                                style={{
                                  padding: "3px 8px", borderRadius: 6, fontSize: 11, fontFamily: "inherit", cursor: "pointer", fontWeight: 600, border: "1px solid",
                                  background: o.fulfillment_status === "fulfilled" ? "#f0fdf4" : o.fulfillment_status === "partial" ? "#fffbeb" : "#f3f4f6",
                                  color:      o.fulfillment_status === "fulfilled" ? "#15803d" : o.fulfillment_status === "partial" ? "#b45309" : "#374151",
                                  borderColor:o.fulfillment_status === "fulfilled" ? "#bbf7d0" : o.fulfillment_status === "partial" ? "#fde68a" : "#d1d5db",
                                }}>
                                <option value="unfulfilled">Unfulfilled</option>
                                <option value="partial">Partial</option>
                                <option value="fulfilled">Fulfilled</option>
                              </select>
                            </td>
                            <td style={S.td}>
                              <div style={{ display:"flex", gap:5 }}>
                                <button style={{ padding:"4px 8px", borderRadius:6, fontSize:11, cursor:"pointer", background:"#F47920", color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600, opacity: o._platform==="amazon"&&!o.line_items?.length?0.4:1 }}
                                  onClick={() => { setInvoiceOrder(o); setPage("invoicepdf"); }}
                                  title={o._platform==="amazon"?"Amazon order — line items load separately":""}>
                                  🧾
                                </button>
                                <button style={{ padding:"4px 8px", borderRadius:6, fontSize:11, cursor:"pointer", background:"#1A3A5C", color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }}
                                  title="Print shipping label"
                                  onClick={() => printOrderLabels([o])}>
                                  🏷️
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {orders.length===0 && <tr><td colSpan={8} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:40 }}>No orders loaded</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}


              {page==="invoices" && (
                <div style={S.card}>
                  <div style={S.cardHdr}><span>Invoices ({invoices.length})</span><button style={S.btn()} onClick={() => { setInvProd(products[0]); setInvModal(true); }}>+ New</button></div>
                  {invoices.length===0 ? (
                    <div style={{ textAlign:"center", padding:50, color:"#9ca3af" }}><div style={{ fontSize:36, marginBottom:10 }}>🧾</div>No invoices yet</div>
                  ) : (
                    <div style={{ overflowX:"auto" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse" }}>
                        <thead><tr>{["Invoice ID","Date","Customer","Product","Channel","Qty","Total (GST incl.)"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {invoices.map((inv) => {
                            const pl = PLATFORMS.find((p) => p.id===inv.platform);
                            return (
                              <tr key={inv.id} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                                <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{inv.id}</td>
                                <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{inv.date}</td>
                                <td style={{ ...S.td, fontWeight:500 }}>{inv.customer}</td>
                                <td style={S.td}>{inv.productName}</td>
                                <td style={S.td}>{pl && <Badge label={pl.name} color={pl.color} bg={pl.bg}/>}</td>
                                <td style={S.td}>{inv.qty}</td>
                                <td style={{ ...S.td, fontWeight:700, color:"#F47920" }}>{INR(inv.total)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* CHANNELS */}
              {page==="channels" && (
                <>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
                    {PLATFORMS.map((pl) => {
                      const dot   = pl.id==="myntra" ? statusDot("idle") : statusDot(syncStatus[pl.id]);
                      const stock = products.reduce((a,p) => a+(p.inventory[pl.id]||0),0);
                      const isConn= tenant?.platforms?.[pl.id]?.connected;
                      return (
                        <div key={pl.id} style={{ ...S.card, borderTop:`3px solid ${pl.color}`, padding:16, marginBottom:0 }}>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
                            <span style={{ fontWeight:600, color:pl.color, fontSize:14 }}>{pl.name}</span>
                            <span style={{ fontSize:10, color:dot.color, fontWeight:600 }}>● {dot.label}</span>
                          </div>
                          {!isConn && <div style={{ fontSize:11, color:"#d97706", marginBottom:8 }}>⚠ Not connected — go to Settings</div>}
                          {[["Stock",stock],["Listed",products.filter((p)=>p.inventory[pl.id]>0).length],["Low",products.filter((p)=>p.inventory[pl.id]>0&&p.inventory[pl.id]<=10).length]].map(([l,v]) => (
                            <div key={l} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:5 }}><span style={{ color:"#6b7280" }}>{l}</span><span style={{ fontWeight:600 }}>{v}</span></div>
                          ))}
                          <button style={{ ...S.btn(pl.color), width:"100%", marginTop:10 }} onClick={() => { loadAll(); toast(`${pl.name} synced ✓`, "success"); }}>↻ Sync</button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* SETTINGS */}
              {page==="purchases" && <PagePurchaseOrders toast={toast} products={products}/>}
              {page==="returns" && <PageReturns toast={toast} products={products} orders={orders}/>}
              {page==="sourcing" && <PageSourcing toast={toast}/>}
              {page==="gst" && <PageGST toast={toast} orders={orders} products={products}/>}
              {page==="myntraimport" && <PageMyntraImport toast={toast} products={products}/>}
              {page==="myntrapnl" && <PageMyntraPnL toast={toast}/>}
              {page==="marketplaceimport" && <PageMarketplaceImport toast={toast}/>}
              {page==="skumapping" && <PageSkuMapping toast={toast} products={products}/>}
              {page==="logistics" && <PageLogistics toast={toast} orders={orders} products={products}/>}
              {page==="billing" && <PageBilling toast={toast} tenant={tenant} onPlanUpgrade={(plan, limits)=> setTenant((t) => ({ ...t, plan, limits }))}/>}
              {page==="invoicepdf" && <PageInvoicePDF toast={toast} products={products} orders={orders} invoices={invoices} tenant={tenant} invoiceOrder={invoiceOrder} onClearOrder={()=>setInvoiceOrder(null)}/>}
              {page==="wms" && <PageWMS toast={toast} products={products} orders={orders}/>}
              {page==="pim" && <PagePIM toast={toast}/>}
                            {page==="myntraImages" && <PageMyntraImages toast={toast}/>}
              {page==="accounting" && <PageAccounting toast={toast} invoices={invoices} orders={[]} tenant={tenant}/>}
              {page==="settings" && <PageSettings tenant={tenant} onLogout={handleLogout} toast={toast}/>}
              {page==="whatsapp" && <PageWhatsApp toast={toast} products={products} orders={orders}/>}
              {page==="reconciliation" && <PageReconciliation toast={toast}/>}
              {page==="automation" && <PageAutomation toast={toast}/>}
              {page==="reports" && <PageReports toast={toast} orders={orders} products={products} returns={[]}/>}
              {page==="team" && <PageTeam toast={toast} tenant={tenant}/>}
              {page==="profile" && <PageProfile toast={toast} tenant={tenant} onTenantUpdate={(t) => setTenant(t)} onLogout={handleLogout}/>}  
              {page==="labels" && <PageLabels toast={toast} orders={orders} tenant={tenant}/>}
              {page === "expenses" && <PageExpenses toast={toast} />}
              {page === "imagetools" && <PageImageTools toast={toast} />}
              {page === "tasks" && <PageTasks toast={toast} tenant={tenant} />}
              </>
              )}
           </>
          )}
        </div>
      </div>

      


      {/* ── URGENT TASKS FLOATING WIDGET ── */}
      {urgentTasks.length > 0 && showUrgent && tenant && (
        <div style={{
          position:     "fixed",
          bottom:       24,
          right:        24,
          width:        urgentMinimized ? 180 : 280,
          background:   "#fff",
          borderRadius: 12,
          boxShadow:    "0 8px 32px rgba(220,38,38,0.18), 0 2px 8px rgba(0,0,0,0.1)",
          border:       "2px solid #fecaca",
          zIndex:       9998,
          overflow:     "hidden",
          transition:   "all .25s ease",
        }}>
          {/* Header */}
          <div style={{ background:"linear-gradient(135deg,#dc2626,#ef4444)", padding:"10px 12px", display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}
            onClick={() => setUrgentMinimized((p) => !p)}>
            <div style={{ display:"flex", alignItems:"center", gap:7 }}>
              <span style={{ fontSize:16, animation:"pulse 1.5s infinite" }}>🚨</span>
              <span style={{ fontWeight:700, fontSize:12, color:"#fff" }}>
                {urgentTasks.length} Urgent Task{urgentTasks.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div style={{ display:"flex", gap:4 }}>
              <span style={{ color:"rgba(255,255,255,0.8)", fontSize:14, fontWeight:700 }}>
                {urgentMinimized ? "▲" : "▼"}
              </span>
              <button onClick={(e) => { e.stopPropagation(); setShowUrgent(false); }}
                style={{ background:"rgba(255,255,255,0.2)", border:"none", color:"#fff", borderRadius:4, width:18, height:18, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1 }}>
                ✕
              </button>
            </div>
          </div>

          {/* Task list */}
          {!urgentMinimized && (
            <div style={{ maxHeight:240, overflowY:"auto" }}>
              {urgentTasks.slice(0,5).map((task, i) => (
                <div key={task._id} style={{ padding:"9px 12px", borderBottom:"1px solid #fef2f2", cursor:"pointer", transition:"background .1s" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#fff5f5"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                  onClick={() => setPage("tasks")}>
                  <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
                    <span style={{ fontSize:14, flexShrink:0, marginTop:1 }}>
                      {task.status === "in_progress" ? "⚡" : "🔴"}
                    </span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:600, fontSize:12, color:"#1A3A5C", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {task.title}
                      </div>
                      <div style={{ fontSize:10, color:"#9ca3af", marginTop:2, display:"flex", gap:6 }}>
                        {task.category && <span>{task.category}</span>}
                        {task.dueDate && (
                          <span style={{ color: new Date(task.dueDate) < new Date() ? "#dc2626" : "#d97706", fontWeight:600 }}>
                            {new Date(task.dueDate) < new Date() ? "⚠ Overdue" : "Due " + new Date(task.dueDate).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {urgentTasks.length > 5 && (
                <div style={{ padding:"7px 12px", fontSize:11, color:"#dc2626", fontWeight:600, textAlign:"center", background:"#fff5f5" }}>
                  +{urgentTasks.length - 5} more urgent tasks
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          {!urgentMinimized && (
            <div style={{ padding:"8px 12px", borderTop:"1px solid #fef2f2" }}>
              <button onClick={() => setPage("tasks")}
                style={{ width:"100%", padding:"6px", borderRadius:6, background:"#dc2626", color:"#fff", border:"none", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                View All Tasks →
              </button>
            </div>
          )}

          <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }`}</style>
        </div>
      )}

    </div>
  );
}
